<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    Track,
    isE2EESupported,
    type RemoteTrack,
    type RemoteTrackPublication,
    type RemoteParticipant,
  } from "livekit-client";
  import type { E2eeRequiredResponse } from "@diy-avatar/shared/api";
  import {
    fetchToken,
    listRooms,
    logout,
    ApiError,
    getMyProfileImage,
    getStudentProfileImage,
    setMyProfileImage,
    reportE2eeKeyless,
  } from "../lib/api";
  import {
    SubscriberClient,
    DEVICE_IDENTITY_PREFIX,
    DEFAULT_CAMERA_LABEL,
    type ConnectionState,
  } from "../lib/livekit";
  import type { RoomSummary } from "@diy-avatar/shared/api";
  import { session, reset, setError } from "../lib/session.svelte";
  import ChangePassword from "./ChangePassword.svelte";
  import { StudentControls, RAISE_HAND_TIMEOUT_MS } from "../lib/controls.svelte";
  import { t, _ } from "../lib/i18n";
  import Icon from "../lib/Icon.svelte";
  import Avatar from "../lib/Avatar.svelte";
  import ProfileImagePrompt from "../lib/ProfileImagePrompt.svelte";
  import EncryptionStatus from "../lib/EncryptionStatus.svelte";
  import DismissibleBanner from "../lib/DismissibleBanner.svelte";
  import ClassPreview from "../lib/ClassPreview.svelte";
  import QrScanner from "../lib/QrScanner.svelte";
  import { overlayState } from "../lib/e2eeOverlay";
  import { e2eeStatus } from "../lib/api";
  import {
    provisionKeyFromQrText,
    provisionKeyFromManualText,
    type ProvisionResult,
  } from "../lib/e2eeProvision";
  import {
    loadSharedKeys,
    rememberMediaKey,
    getMediaKeyId,
    isRecoveryNeeded,
    recoverWithCode,
    hasIdentity,
    getMyPublicKey,
    DEVICE_KEY_SCOPE,
    DEVICE_KEY_SCOPE_ID,
  } from "../lib/e2eeSession";
  // E2EE keyless-recovery round-trip: stash the room before bouncing to Login,
  // then auto-rejoin it on return (see repairEncryption + onMount).
  import {
    setPendingReturnRoom,
    takePendingReturnRoom,
  } from "../lib/e2eeRecoveryReturn";
  import { getProvisionFailed } from "../lib/e2eeProvisionState";
  import { backoffOnSuccess, backoffOnFailure } from "../lib/pollBackoff";
  import { decodeIncoming, type DcEnvelope } from "@diy-avatar/shared";
  import type { EncryptionMode } from "@diy-avatar/shared/api";
  import {
    attachWebrtcDiagnostics,
    logDiagBreadcrumb,
  } from "@diy-avatar/shared/log_shipper_browser";
  import {
    classifyConnectError,
    type ConnectErrorInfo,
  } from "@diy-avatar/shared/connect_error";

  // ── E2EE policy + UX (Phase 5) ─────────────────────────────────────────────
  // All inert unless the global flag is on. When off, NO E2EE UI renders and no
  // E2EE network calls are made.
  let e2eeEnabled = false;
  let showQrScanner = false;
  // Bug 5: the lock icon opens this centered settings popup instead of toggling
  // E2EE directly.
  let showE2eeSettings = false;
  // Big in-app help/doc popup (opened from the "key changed" rekey popup).
  let showE2eeHelp = false;
  let e2eeMsg: string | null = null;
  // Persistent scan-result toast: rendered OUTSIDE the scanner modal so the
  // result survives the modal closing (the in-modal message unmounts on close).
  // Non-blocking (pointer-events:none) and auto-dismisses after a few seconds.
  let e2eeToast: string | null = null;
  let e2eeToastTone: "info" | "success" = "info";
  let e2eeToastTimer: ReturnType<typeof setTimeout> | null = null;
  // True while toggleE2ee() is awaiting the background key install so the UI can
  // show a spinner + lock the button instead of appearing to do nothing.
  let e2eeToggleBusy = false;
  function showE2eeToast(msg: string, tone: "info" | "success" = "info"): void {
    e2eeToast = msg;
    e2eeToastTone = tone;
    if (e2eeToastTimer) clearTimeout(e2eeToastTimer);
    e2eeToastTimer = setTimeout(() => {
      e2eeToast = null;
      e2eeToastTimer = null;
    }, 4000);
  }
  // First-run vs rotation: true once the student has ever held a media key
  // (either already stored on init, or after a successful scan). Drives the
  // scanner modal wording — first-run "enable encryption" vs "key changed".
  let everHadKey = false;
  // Runtime client capability (isE2EESupported()) — the SOURCE OF TRUTH for
  // whether this client can do insertable-streams E2EE. Sent to the token
  // endpoint so the backend can enforce the join policy.
  const e2eeClientSupports =
    typeof isE2EESupported === "function" ? isE2EESupported() : false;
  // Negotiated whole-session state from the token response: whether E2EE is
  // active for the session, and whether the device FORCES it (opt-out locked).
  let e2eeSessionActive = false;
  let e2eeSessionForced = false;
  // FIX B tri-state: forced status is UNKNOWN until the token response lands.
  // The plaintext-fallback button must NOT render pre-connect (treating unknown
  // as not-forced would wrongly offer a plaintext escape in a forced room), so
  // we gate it on "known-not-forced" rather than `!e2eeForced` (false default).
  let e2eeForcedKnown = false;
  // P4: room/hierarchy forces in-person QR key exchange (auto-seal disabled).
  // When set, the student must scan the device QR — no silent auto-load.
  let forceE2eeQr = false;
  // Phase 6: the negotiated session's active device key_id (from the token). The
  // student compares it against the key it actually holds; a mismatch (or no key)
  // while the session wants E2EE means the key ROTATED → re-scan the new QR.
  let e2eeSessionKeyId: string | null = null;
  // Phase 2: the negotiated whole-room encryption mode from the token
  // (`e2ee_session.encryption_mode`). Absent ⇒ "encrypted" (pre-Phase-2). When
  // "plaintext" the room downgraded (a participant can't decrypt) so a capable
  // student must NOT enable per-frame decryption — the publisher sends plaintext.
  let e2eeSessionMode: EncryptionMode = "encrypted";
  // Phase 1 recovery routing: when login could not unlock the stored identity but
  // a recovery blob exists, we prompt for the RECOVERY CODE (a QR scan can't help
  // without an in-memory identity). Toggled from initE2ee via isRecoveryNeeded().
  let e2eeRecoveryNeeded = false;
  let e2eeRecoveryCode = "";
  let e2eeRecoveryMsg: string | null = null;
  let e2eeRecoveryBusy = false;
  // Reliability: the login-time identity provisioning failed/timed out (or
  // simply produced no in-memory identity) so a QR scan would only yield
  // `noIdentity`. Surfaced as a non-blocking "encryption not ready" state that
  // gates the scan buttons and (in non-forced rooms) offers a plaintext
  // fallback. Set in initE2ee; cleared once an identity exists (e.g. recovery).
  let e2eeNotReady = false;
  // Guard so the automatic plaintext downgrade (genuinely no usable identity in
  // a non-forced room) fires at most once per connected session.
  let e2eeAutoDowngraded = false;
  // Phase 2: guard so the keyless "shouldn't happen" report fires at most once
  // per connected session (the late-key retry exhausting + still no key).
  let e2eeKeylessReported = false;
  // The student's persisted opt-out preference (plaintext when allowed). Kept in
  // localStorage so it survives a reload and is fed into the next token request
  // (e2ee_opt_out) and into whether to auto-enable on connect.
  const E2EE_OPTOUT_KEY = "avatar-e2ee-opt-out";
  let e2eeOptOut = readOptOut();
  // Live per-frame encryption state, driven by the subscriber client AFTER
  // connect (client.isE2EEEnabled()) and re-derived on toggle. Feeds the overlay
  // lock icon. `false` = plaintext, `true` = encrypted.
  let streamEncrypted: boolean | "some" = false;
  // Blocked-join remediation (set when the token endpoint returns e2ee_required).
  // Maps the backend `code` to localized DE/EN copy with an English fallback.
  let e2eeBlocked: E2eeRequiredResponse["remediation"] | null = null;
  // Keyless-recovery UX: the live subscriber raised an EncryptionError (missing
  // key idx) AND we hold no usable media key. Drives the CALM "waiting for key"
  // banner + countdown. Cleared the moment a key becomes live.
  let e2eeKeyMissing = false;
  // Countdown (seconds) for the waiting banner. The device redistributes the
  // media key roughly every 60s, so we count DOWN from 60 and restart while the
  // key is still missing. Driven by the late-key retry cycle.
  let e2eeKeyWaitSeconds = 0;
  let e2eeKeyWaitTimer: ReturnType<typeof setInterval> | null = null;
  // Bounded auto-repair: count still-keyless countdown expiries. After
  // E2EE_MAX_REPAIR_ATTEMPTS cycles we force the Login recovery path so the
  // student is never stranded on an eternal banner. Reset when a key lands.
  let e2eeRepairAttempts = 0;
  const E2EE_MAX_REPAIR_ATTEMPTS = 2;
  // FIX B: bounded auto-recovery for the `notReady` state (no in-memory identity
  // / no installed media key). Previously notReady had NO countdown and never
  // auto-cleared, deadlocking the student. We now mirror the keyMissing pattern:
  // count DOWN, on expiry attempt a refetch identity + loadSharedKeys +
  // applyMediaKey, then (if still keyless) disconnect+rejoin for a FRESH token,
  // capped at E2EE_MAX_REPAIR_ATTEMPTS. After the cap we surface a clear TERMINAL
  // message instead of a silent spinner.
  let e2eeNotReadySeconds = 0;
  let e2eeNotReadyTimer: ReturnType<typeof setInterval> | null = null;
  let e2eeNotReadyAttempts = 0;
  // True once the bounded notReady recovery exhausted its attempts: the banner
  // then shows an actionable terminal message rather than an endless countdown.
  let e2eeNotReadyTerminal = false;
  // De-dup guard for the keyless observability breadcrumb (re-armed on key land).
  let e2eeKeylessLogged = false;
  // Manual key entry inside the scan/rekey modals: a paste-the-code fallback for
  // when the camera fails or isn't available. `e2eeManualMode` toggles between
  // the live <QrScanner> and the <textarea>; `e2eeManualKey` holds the pasted text.
  let e2eeManualMode = false;
  let e2eeManualKey = "";
  // Tame-the-red-banners: local dismiss flags so the student can collapse the
  // permanent plaintext-downgrade / not-ready alerts to a small pill.
  let e2eePlaintextBannerDismissed = false;
  let e2eeKeyMissingBannerDismissed = false;
  // Auto-return guard: the post-Login keyless-recovery rejoin fires at most once.
  let e2eeReturnHandled = false;

  function readOptOut(): boolean {
    try {
      return localStorage.getItem(E2EE_OPTOUT_KEY) === "1";
    } catch {
      return false;
    }
  }
  function writeOptOut(v: boolean): void {
    try {
      if (v) localStorage.setItem(E2EE_OPTOUT_KEY, "1");
      else localStorage.removeItem(E2EE_OPTOUT_KEY);
    } catch {
      /* ignore storage failures (private mode etc.) */
    }
  }

  async function initE2ee(): Promise<void> {
    try {
      const st = await e2eeStatus();
      e2eeEnabled = st.enabled === true;
      if (!e2eeEnabled) return;
      // Phase 1 recovery routing: if login could not unlock the stored identity
      // (rotated wrapping key) but a recovery blob exists, the in-memory identity
      // is null — a QR scan can't help. Surface the RECOVERY-CODE prompt instead.
      e2eeRecoveryNeeded = isRecoveryNeeded();
      // Load + decrypt any stored keys with the session identity (provisioned at
      // login) so they are available on every device.
      await loadSharedKeys().catch(() => undefined);
      // If a device media key is already held, this is NOT a first run — any
      // later re-scan is a rotation, so use the "key changed" wording.
      if (getMediaKeyId(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID)) everHadKey = true;
      // Reliability gate: when E2EE is on but there is NO in-memory identity and
      // recovery is NOT pending (login provisioning failed/timed out), a QR scan
      // can only return `noIdentity`. Surface a non-blocking "not ready" state.
      // Class can't re-provision from a password, so the remedy is recovery (if
      // available) or the plaintext fallback below.
      e2eeNotReady = !hasIdentity() && !e2eeRecoveryNeeded;
      if (e2eeNotReady || getProvisionFailed()) {
        // Genuinely no usable identity and recovery is impossible → in a
        // NON-forced room, auto-fall back to plaintext so the student can still
        // watch (the server downgrades + the kiosk drives the reconnect).
        maybeAutoDowngradeNoIdentity();
      }
    } catch {
      e2eeEnabled = false;
    }
  }

  /** True when the negotiated session FORCES E2EE (no plaintext fallback). */
  function isE2eeForced(): boolean {
    return e2eeSessionForced || forceE2eeQr;
  }

  /**
   * Automatic plaintext fallback when there is genuinely NO usable identity and
   * recovery is NOT possible (no in-memory identity, recovery not pending) in a
   * NON-FORCED room. Calls reportE2eeKeyless so the server downgrades the room
   * to plaintext; the kiosk then broadcasts the `encryption_mode` envelope which
   * drives the existing reconnect. In a FORCED room we do NOT downgrade — the
   * existing block / QR-required behavior stands. Fires at most once per session.
   */
  function maybeAutoDowngradeNoIdentity(): void {
    if (!e2eeEnabled) return;
    if (hasIdentity() || e2eeRecoveryNeeded) return; // recoverable → don't downgrade
    if (isE2eeForced()) return; // forced room: keep security, never downgrade
    if (e2eeSessionMode === "plaintext") return; // already downgraded
    if (e2eeAutoDowngraded) return;
    if (connectedRoom == null) return; // need a room to report against
    e2eeAutoDowngraded = true;
    void reportE2eeKeyless({
      room: connectedRoom,
      expected_key_id: e2eeSessionKeyId,
    }).catch(() => undefined);
  }

  /**
   * User-controlled plaintext fallback ("Continue unencrypted"). For a student
   * with a broken/missing key in a NON-forced room: report keyless so the server
   * downgrades the room to plaintext (the kiosk then drives the reconnect via the
   * `encryption_mode` envelope), then close any open dialog and clear the e2ee
   * error/notice. No-op in a FORCED room (button is hidden there too).
   */
  async function continueUnencrypted(): Promise<void> {
    if (!e2eeEnabled || isE2eeForced()) return;
    if (connectedRoom != null && !e2eeAutoDowngraded) {
      e2eeAutoDowngraded = true;
      try {
        await reportE2eeKeyless({
          room: connectedRoom,
          expected_key_id: e2eeSessionKeyId,
        });
      } catch {
        /* best-effort: inert (404) when the flag is off */
      }
    }
    // Close every e2ee dialog + clear any error/notice so the student isn't left
    // staring at a "scan required" / recovery prompt while we reconnect plain.
    showQrScanner = false;
    showE2eeSettings = false;
    e2eeManualMode = false;
    e2eeRecoveryNeeded = false;
    e2eeNotReady = false;
    e2eeMsg = null;
    e2eeToast = null;
  }

  // Known remediation codes the frontend localizes; any other code falls back
  // to the backend-provided English `title`/`detail`.
  function blockedTitle(rem: E2eeRequiredResponse["remediation"]): string {
    return rem.code === "e2ee_unsupported"
      ? t("e2ee.blocked.title")
      : rem.title;
  }
  function blockedDetail(rem: E2eeRequiredResponse["remediation"]): string {
    return rem.code === "e2ee_unsupported"
      ? t("e2ee.blocked.detail")
      : rem.detail;
  }

  /**
   * Phase 1 recovery routing: restore the original identity from the student's
   * recovery code. `recoverWithCode` installs the recovered keypair in memory
   * (the thing we need for silent auto-decrypt THIS session) and re-wraps the
   * persisted copy under the supplied secret. Class.svelte does not hold the
   * login password, so we re-wrap under the recovery code itself — a stable
   * secret the student already holds — which keeps the identity recoverable; a
   * later password change re-wraps it under the real password again.
   */
  async function submitRecoveryCode(): Promise<void> {
    const code = e2eeRecoveryCode.trim();
    if (!code || e2eeRecoveryBusy) return;
    e2eeRecoveryBusy = true;
    e2eeRecoveryMsg = null;
    try {
      // Recover the in-memory identity. `recoverWithCode` re-wraps + persists the
      // recovered key under the supplied secret so future unwraps work; we reuse
      // the recovery code as that secret (the student holds it). On success the
      // previously-sealed device keys decrypt and install.
      const ok = await recoverWithCode(code, code);
      if (!ok) {
        e2eeRecoveryMsg = $_("e2ee.recovery.fail");
        return;
      }
      e2eeRecoveryNeeded = false;
      e2eeNotReady = false;
      e2eeRecoveryCode = "";
      // Make any recovered device key live on the current subscriber + refresh.
      try {
        await client?.applyMediaKey(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID);
      } catch {
        /* best-effort */
      }
      if (getMediaKeyId(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID)) everHadKey = true;
      refreshEncryptedState();
      e2eeRecoveryMsg = $_("e2ee.recovery.ok");
    } catch {
      e2eeRecoveryMsg = $_("e2ee.recovery.fail");
    } finally {
      e2eeRecoveryBusy = false;
    }
  }

  // P3 (Option A): the kiosk auto-seals the media key, but a student who
  // connected BEFORE the seal landed (or who provisioned their identity late)
  // won't hold it yet. Re-pull GET /me/e2ee/keys on a short timer post-connect
  // so a late-sealed key installs silently — the lock turns green with no scan.
  let e2eeRetryTimer: ReturnType<typeof setInterval> | null = null;
  function scheduleLateKeyRetry(): void {
    if (!e2eeEnabled || !e2eeClientSupports) return;
    if (e2eeRetryTimer !== null) return;
    let tries = 0;
    e2eeRetryTimer = setInterval(() => {
      tries += 1;
      const haveKey = getMediaKeyId(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID) != null;
      if (haveKey || tries >= 10 || state !== "connected") {
        if (e2eeRetryTimer !== null) clearInterval(e2eeRetryTimer);
        e2eeRetryTimer = null;
        // Phase 2: the retry window expired and we STILL hold no usable
        // device-scope key while the session wants E2EE active (encrypted mode).
        // This SHOULDN'T happen with auto-seal — report it loudly so the server
        // downgrades the whole non-forced room to plaintext and audits the
        // failure. Only the genuine keyless-after-heal case (not a rotation
        // re-scan, not QR-forced, not already plaintext) qualifies.
        if (
          !haveKey &&
          state === "connected" &&
          (e2eeSessionActive || e2eeSessionForced) &&
          e2eeSessionMode === "encrypted" &&
          !forceE2eeQr
        ) {
          reportKeyless();
        }
        // Still keyless while the banner is up → kick the bounded auto-repair
        // so the waiting state heals (or bounces) instead of being stranded.
        if (
          !haveKey &&
          e2eeKeyMissing === true &&
          state === "connected" &&
          e2eeSessionMode !== "plaintext"
        ) {
          autoRepairOnTimeout();
        }
        return;
      }
      void loadSharedKeys()
        .then(() => client?.applyMediaKey(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID))
        .then(() => refreshEncryptedState())
        .catch(() => undefined);
    }, 3000);
  }

  /**
   * Phase 2: report the "capable-but-keyless-after-heal" anomaly to the server
   * exactly once per connected session. The server marks this student keyless
   * for the room (driving the non-forced downgrade) and audits
   * `e2ee_autoseal_failed`. Best-effort: inert (404) when the flag is off.
   */
  function reportKeyless(): void {
    if (e2eeKeylessReported || connectedRoom == null) return;
    e2eeKeylessReported = true;
    // FIX C observability: this path is otherwise SILENT (the e2ee worker only
    // logs "No key set found" per frame). Emit a structured breadcrumb via the
    // existing log-shipper so a keyless-after-heal anomaly is diagnosable, with
    // the expected key_id and the fact that no media key is installed.
    logKeylessBreadcrumb("keyless after heal");
    void reportE2eeKeyless({
      room: connectedRoom,
      expected_key_id: e2eeSessionKeyId,
    }).catch(() => undefined);
  }

  /**
   * FIX C observability: ship a structured breadcrumb whenever the student is
   * connected, encryption is expected, and we end up keyless / "No key set
   * found". Reuses the same log-shipper transport as the "join room" / WebRTC
   * diagnostics breadcrumbs (no new transport). Best-effort + de-duped per
   * connected session so the per-frame EncryptionError flood can't spam it.
   */
  function logKeylessBreadcrumb(reason: string): void {
    if (e2eeKeylessLogged) return;
    e2eeKeylessLogged = true;
    try {
      logDiagBreadcrumb("e2ee keyless", {
        reason,
        room: connectedRoom,
        expected_key_id: e2eeSessionKeyId,
        media_key_installed: client?.hasMediaKey() === true,
        session_mode: e2eeSessionMode,
        forced: isE2eeForced(),
      });
    } catch {
      /* never let observability break the request */
    }
  }

  /** Recompute the overlay lock state from the live client + negotiated state. */
  function refreshEncryptedState(): void {
    // Bump so the reactive `e2eeStaleKey` recomputes against the latest key set.
    staleKeyVersion += 1;
    if (!e2eeEnabled || !e2eeClientSupports) {
      streamEncrypted = false;
      return;
    }
    streamEncrypted = client?.isE2EEEnabled() === true;
    // Keyless-recovery UX: a live-encrypted stream means the key landed → clear
    // the "waiting for key" state + stop the countdown.
    if (streamEncrypted === true || client?.hasMediaKey() === true) {
      e2eeKeyMissing = false;
      stopKeyWaitCountdown();
      e2eeRepairAttempts = 0;
      stopNotReadyCountdown();
      e2eeNotReadyAttempts = 0;
      e2eeNotReadyTerminal = false;
      // Key landed → re-arm the keyless breadcrumb for any future drop.
      e2eeKeylessLogged = false;
    }
  }

  // ── Keyless-recovery UX: "waiting for key" countdown ───────────────────────
  // The device redistributes the media key roughly every 60s, so the banner
  // counts DOWN from 60 and, while the key is still missing, restarts for the
  // next redistribution cycle. Cheap + idempotent (the EncryptionError event can
  // fire per frame, so the handler must never start a second timer).
  function startKeyWaitCountdown(): void {
    if (e2eeKeyWaitTimer !== null) return; // already running
    e2eeKeyWaitSeconds = 60;
    e2eeKeyWaitTimer = setInterval(() => {
      // Key arrived → refresh clears e2eeKeyMissing AND stops the countdown,
      // dismissing the banner on a successful late key exchange.
      if (client?.hasMediaKey() === true) {
        refreshEncryptedState();
        return;
      }
      // We left the room (or otherwise cleared the waiting state) → stop.
      if (!e2eeKeyMissing) {
        stopKeyWaitCountdown();
        return;
      }
      e2eeKeyWaitSeconds -= 1;
      // Countdown expired → don't loop forever; run bounded auto-repair.
      if (e2eeKeyWaitSeconds <= 0) {
        stopKeyWaitCountdown();
        autoRepairOnTimeout();
        return;
      }
    }, 1000);
  }
  function stopKeyWaitCountdown(): void {
    if (e2eeKeyWaitTimer !== null) {
      clearInterval(e2eeKeyWaitTimer);
      e2eeKeyWaitTimer = null;
    }
    e2eeKeyWaitSeconds = 0;
  }

  // Countdown expired (or late-key retry gave up): bounded auto-repair. Re-pull
  // the key once (repairEncryption smart branch) for the first attempts; after
  // E2EE_MAX_REPAIR_ATTEMPTS still-keyless cycles, force the Login recovery path
  // regardless of identity so the student is never stuck on an eternal banner.
  function autoRepairOnTimeout(): void {
    if (client?.hasMediaKey() === true) {
      refreshEncryptedState();
      return;
    }
    e2eeRepairAttempts += 1;
    if (e2eeRepairAttempts >= E2EE_MAX_REPAIR_ATTEMPTS) {
      // Force the Login bounce: stash the room, disconnect, route to Login.
      if (connectedRoom != null) setPendingReturnRoom(connectedRoom);
      void client?.disconnect().catch(() => undefined);
      push("/");
      return;
    }
    void repairEncryption();
  }

  // ── FIX B: bounded `notReady` auto-recovery ────────────────────────────────
  // The `notReady` state means login provisioning produced NO usable identity /
  // no installed media key. It used to render an eternal "⏳ noch nicht bereit"
  // banner with no countdown, no auto-repair, and a dead "continue unencrypted"
  // button in forced rooms. We now give it the SAME bounded heal-or-bounce cycle
  // as keyMissing: count down, attempt a silent refetch+apply, and if that fails
  // disconnect+rejoin for a FRESH token (reusing the pending-return-room flow).
  /** True when the bounded notReady recovery should be running. */
  function notReadyRecoveryEligible(): boolean {
    return (
      e2eeEnabled &&
      e2eeNotReady &&
      !e2eeRecoveryNeeded &&
      e2eeSessionMode !== "plaintext" &&
      (state === "connected" || state === "connecting") &&
      client?.hasMediaKey() !== true
    );
  }
  function maybeStartNotReadyRecovery(): void {
    if (e2eeNotReadyTerminal) return;
    if (!notReadyRecoveryEligible()) return;
    startNotReadyCountdown();
  }
  function startNotReadyCountdown(): void {
    if (e2eeNotReadyTimer !== null) return; // already running
    e2eeNotReadySeconds = 60;
    e2eeNotReadyTimer = setInterval(() => {
      // Key/identity arrived elsewhere → refresh clears notReady + stops here.
      if (client?.hasMediaKey() === true || hasIdentity()) {
        e2eeNotReady = false;
        stopNotReadyCountdown();
        refreshEncryptedState();
        return;
      }
      // No longer eligible (left room, downgraded, recovery prompt) → stop.
      if (!notReadyRecoveryEligible()) {
        stopNotReadyCountdown();
        return;
      }
      e2eeNotReadySeconds -= 1;
      if (e2eeNotReadySeconds <= 0) {
        stopNotReadyCountdown();
        void recoverNotReadyOnTimeout();
        return;
      }
    }, 1000);
  }
  function stopNotReadyCountdown(): void {
    if (e2eeNotReadyTimer !== null) {
      clearInterval(e2eeNotReadyTimer);
      e2eeNotReadyTimer = null;
    }
    e2eeNotReadySeconds = 0;
  }
  /**
   * Countdown expired: bounded notReady recovery. (a) Re-pull identity-backed
   * shared keys + re-apply the device-scope key on the live subscriber. If a key
   * lands, we are healed. Otherwise (b), for the first attempts, disconnect and
   * rejoin to pull a FRESH token (reusing setPendingReturnRoom / the onMount
   * auto-rejoin used by autoRepairOnTimeout). After E2EE_MAX_REPAIR_ATTEMPTS the
   * notReady banner switches to a clear TERMINAL message instead of spinning.
   */
  async function recoverNotReadyOnTimeout(): Promise<void> {
    if (!e2eeEnabled) return;
    // (a) Silent refetch + apply — heals when login finally provisioned the
    // identity or the kiosk auto-sealed the key after our initial gate.
    await loadSharedKeys().catch(() => undefined);
    if (hasIdentity()) e2eeNotReady = false;
    try {
      await client?.applyMediaKey(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID);
    } catch {
      /* best-effort */
    }
    refreshEncryptedState();
    if (client?.hasMediaKey() === true) {
      e2eeNotReady = false;
      return; // healed
    }
    e2eeNotReadyAttempts += 1;
    if (e2eeNotReadyAttempts >= E2EE_MAX_REPAIR_ATTEMPTS) {
      // Capped: never loop forever. Surface a terminal, actionable message and
      // observe the failure once.
      e2eeNotReadyTerminal = true;
      logKeylessBreadcrumb("notReady recovery exhausted");
      return;
    }
    // (b) Disconnect + rejoin for a FRESH token. Stash the room so onMount /
    // routeGate auto-rejoins it on return (same flow autoRepairOnTimeout uses).
    if (connectedRoom != null) {
      const room = connectedRoom;
      setPendingReturnRoom(room);
      try {
        await client?.disconnect();
      } catch {
        /* ignore */
      }
      client = null;
      e2eeReturnHandled = false; // allow routeGate's one-shot to re-fire
      const next = takePendingReturnRoom();
      if (next != null) {
        // Restart the countdown for the rejoined session; if the fresh token
        // STILL yields no key the next expiry hits the attempt cap.
        selectRoom(next);
        return;
      }
    }
    // No room to rejoin → just keep the countdown going for the next cycle.
    maybeStartNotReadyRecovery();
  }

  /**
   * FIX B: user-triggered "Schlüssel erneuern / Neu verbinden" — the actionable
   * control that REPLACES the dead "continue unencrypted" button in forced /
   * unknown-status rooms. Runs the same recovery path (refetch + apply, else
   * disconnect+rejoin for a fresh token), resetting the bounded attempt counter
   * so the student always has a working manual action. NEVER downgrades.
   */
  async function reprovisionAndReconnect(): Promise<void> {
    if (!e2eeEnabled) return;
    e2eeNotReadyTerminal = false;
    e2eeNotReadyAttempts = 0;
    stopNotReadyCountdown();
    await recoverNotReadyOnTimeout();
  }

  /**
   * Keyless-recovery UX: shared success tail for both the QR scan and the manual
   * paste path. Make the freshly-provisioned key live on the subscriber, refresh
   * the lock, and clear any "waiting for key" state. Returns whether per-frame
   * decryption actually went live (a silent KeyProvider/scope mismatch yields a
   * "stored, not live" toast instead of a false success).
   */
  async function applyProvisionedKey(res: ProvisionResult): Promise<boolean> {
    if (!res.ok) return false;
    // Make the freshly-provisioned key usable immediately (Phase 4 reads it).
    rememberMediaKey(res.scope, res.scope_id, res.key_material, res.key_id);
    // We now hold a key → any future re-scan is a rotation, not first-run.
    everHadKey = true;
    // LATE JOIN: install the key into the live subscriber's KeyProvider + enable
    // per-frame decryption now, without a reconnect. Capture the result so a
    // silent failure is visible instead of looking like success.
    let applied = false;
    try {
      applied = (await client?.applyMediaKey(res.scope, res.scope_id)) === true;
      refreshEncryptedState();
    } catch (err) {
      console.error("[e2ee] applyMediaKey threw", err);
    }
    // Key is now installed → leave the waiting state regardless of live status.
    e2eeKeyMissing = false;
    stopKeyWaitCountdown();
    e2eeRepairAttempts = 0;
    return applied;
  }

  /**
   * Keyless-recovery UX: manual key entry (paste the code) submit handler. Shares
   * the seal+store chain with the QR path via provisionKeyFromManualText, then
   * the common applyProvisionedKey tail. Reachable even when the camera failed.
   */
  async function submitManualKey(): Promise<void> {
    const text = e2eeManualKey.trim();
    if (!text) return;
    e2eeMsg = null;
    try {
      const res = await provisionKeyFromManualText(text);
      if (!res.ok) {
        e2eeMsg = $_(`e2ee.scan.${res.reason}`);
        return;
      }
      const applied = await applyProvisionedKey(res);
      const okMsg = applied
        ? $_("e2ee.scan.stored")
        : $_("e2ee.scan.storedNotLive");
      e2eeMsg = okMsg;
      // Close the scanner + clear the field on success.
      showQrScanner = false;
      e2eeManualKey = "";
      e2eeManualMode = false;
      showE2eeToast(okMsg);
    } catch (err) {
      console.error("[e2ee] manual key handler error", err);
      e2eeMsg = $_("e2ee.scan.error");
    }
  }

  /**
   * Keyless-recovery hub action ("Verschlüsselung reparieren"). Branches on
   * whether the in-memory identity exists:
   *   - identity present → the key is just missing: re-pull the shared keys,
   *     re-apply the device-scope key on the live subscriber, restart the
   *     waiting countdown, and ensure the late-key retry loop is running. Stay
   *     in the room (no navigation).
   *   - NO identity → login provisioning failed; a scan/paste can't help (they
   *     need the identity). Stash the room and bounce to Login so the student
   *     re-enters their password; onMount auto-rejoins the room on return.
   */
  async function repairEncryption(): Promise<void> {
    if (!e2eeEnabled) return;
    const haveIdentity = hasIdentity() || getMyPublicKey() != null;
    if (haveIdentity) {
      // Identity exists → the key is recoverable from the shared key set.
      await loadSharedKeys().catch(() => undefined);
      try {
        await client?.applyMediaKey(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID);
      } catch {
        /* best-effort */
      }
      refreshEncryptedState();
      // Still no key → keep the calm waiting banner/countdown visible and make
      // sure the silent late-key retry loop is running so it heals on its own.
      if (client?.hasMediaKey() !== true) {
        e2eeKeyMissing = true;
        startKeyWaitCountdown();
        scheduleLateKeyRetry();
      }
      return;
    }
    // No identity: the password-derived identity must be re-provisioned at Login.
    // Stash the room so we can auto-rejoin after a successful re-login.
    if (connectedRoom != null) setPendingReturnRoom(connectedRoom);
    // Leave gracefully so we are not stuck holding a half-encrypted session.
    try {
      await client?.disconnect();
    } catch {
      /* ignore */
    }
    push("/");
  }

  // Phase 6: the student needs to re-scan when the session wants E2EE active but
  // we do NOT hold the key the device is currently using. Two cases unified:
  //   - rotation: stored key_id differs from the negotiated session key_id.
  //   - late join: we hold no key for the device scope at all.
  // Bumped on connect, after a scan, and on the streamEncrypted refresh.
  let staleKeyVersion = 0;
  $: e2eeStaleKey = (() => {
    // Touch the version so this recomputes after applyMediaKey/scan/connect.
    void staleKeyVersion;
    if (!e2eeEnabled || !e2eeClientSupports) return false;
    // Phase 1 recovery routing: a stale key with NO in-memory identity cannot be
    // fixed by a QR scan — the recovery-code prompt handles this case instead.
    if (e2eeRecoveryNeeded) return false;
    // Phase 2: in a downgraded (plaintext) room a capable student decrypts
    // nothing — there is no key to be "stale". Suppress the re-scan prompt.
    if (e2eeSessionMode === "plaintext") return false;
    // Only meaningful when the session actually wants E2EE on.
    if (!e2eeSessionActive && !e2eeSessionForced) return false;
    // Already decrypting live → not stale.
    if (streamEncrypted === true) return false;
    const held = getMediaKeyId(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID);
    if (!held) return true; // no usable key yet (late join / rotated away)
    // Have a key, but the session advertises a DIFFERENT key_id → rotated.
    if (e2eeSessionKeyId && held !== e2eeSessionKeyId) return true;
    return false;
  })();

  // Reliability: the negotiated session forces E2EE (no plaintext fallback
  // allowed). Drives whether the "Continue unencrypted" button shows.
  $: e2eeForced = e2eeSessionForced || forceE2eeQr;
  // Scan is futile without an in-memory identity (would only return
  // `noIdentity`). Gate the scan triggers while preparing/unrecoverable.
  $: e2eeScanDisabled = e2eeEnabled && e2eeNotReady;

  // True overlay lock disposition for the icon (pure helper, unit-tested):
  // "unavailable" when the client cannot do E2EE, "rekey-needed" when the active
  // session key rotated/late-join (re-scan), "forced" when the device forces it
  // (toggle locked), else a togglable encrypted/plaintext state.
  $: e2eeOverlayState = overlayState({
    enabled: e2eeEnabled,
    clientSupports: e2eeClientSupports,
    forced: e2eeSessionForced,
    encrypted: streamEncrypted === true,
    staleKey: e2eeStaleKey,
  });

  // Which help section matches the student's CURRENT situation (highlighted in
  // the help doc). forceE2eeQr is a separate join-policy flag not folded into
  // overlayState, so it is checked explicitly.
  $: e2eeHelpCurrent =
    e2eeOverlayState === "rekey-needed"
      ? "rekey"
      : e2eeOverlayState === "unavailable"
        ? "unsupported"
        : forceE2eeQr
          ? "qr"
          : e2eeOverlayState === "forced"
            ? "forced"
            : "optional";

  // Bug 5: localized status line for the E2EE settings popup, derived from the
  // overlay disposition (encrypted / plain / forced / rekey-needed / unavailable).
  $: e2eeSettingsStatus =
    e2eeOverlayState === "unavailable"
      ? $_("e2ee.settings.statusUnavailable")
      : e2eeOverlayState === "rekey-needed"
        ? $_("e2ee.settings.statusRekey")
        : e2eeOverlayState === "forced"
          ? $_("e2ee.settings.statusForced")
          : e2eeOverlayState === "encrypted"
            ? $_("e2ee.settings.statusEncrypted")
            : $_("e2ee.settings.statusPlain");

  /**
   * Overlay icon click = the opt-out control. Toggles E2EE on/off AT RUNTIME —
   * but ONLY when not forced and the client is capable. Persists the opt-out so
   * it survives reload and is sent in the next token request.
   */
  async function toggleE2ee(): Promise<void> {
    if (!e2eeEnabled || !e2eeClientSupports || e2eeSessionForced) return;
    const wantOn = !(client?.isE2EEEnabled() === true);
    // Only the enable path performs a (potentially slow) background key install;
    // show the spinner + lock the button so the action isn't invisible.
    if (wantOn) e2eeToggleBusy = true;
    try {
      await client?.setE2EEEnabled(wantOn);
    } catch {
      /* best-effort; refresh below reflects actual state */
    } finally {
      e2eeToggleBusy = false;
    }
    // Opt-out is the inverse of "want encryption on".
    e2eeOptOut = !wantOn;
    writeOptOut(e2eeOptOut);
    e2eeSessionActive = client?.isE2EEEnabled() === true;
    refreshEncryptedState();
    if (wantOn && client?.hasMediaKey() !== true) {
      // Dead-end fix: when enabling encryption leaves us WITHOUT a usable media
      // key, don't silently stay unencrypted — open the obtain-key flow so the
      // student can scan/paste the device code (or repair) instead.
      e2eeManualMode = false;
      showE2eeSettings = false;
      showQrScanner = true;
    } else if (wantOn && client?.isE2EEEnabled() === true) {
      // Success with key already held: the key exchange completed silently, so
      // confirm it visibly — auto-close the popup + green auto-hiding toast.
      showE2eeSettings = false;
      showE2eeToast($_("e2ee.settings.enabledToast"), "success");
    }
  }

  // Reasons the parent can fix on the spot by re-aiming the camera (a bad/partial
  // read) or by scanning a freshly regenerated code (expired) → keep the scanner
  // OPEN for a retry. Every other reason (identity/trust/server) plus any
  // unexpected throw is not retry-by-rescan, so the scanner CLOSES.
  const QR_SCAN_RETRYABLE = new Set(["invalid", "expired"]);

  async function onQrScan(e: CustomEvent<string>): Promise<void> {
    e2eeMsg = null;
    console.debug("[e2ee] QR scan received", { len: e.detail?.length ?? 0 });
    try {
      // Browser-agnostic seal+store chain (parse → verify → re-seal → POST),
      // shared with the Profile provisioning UI (see e2eeProvision.ts).
      const res = await provisionKeyFromQrText(e.detail);
      if (!res.ok) {
        console.warn("[e2ee] provisioning failed", { reason: res.reason });
        const msg = $_(`e2ee.scan.${res.reason}`);
        e2eeMsg = msg;
        // Retryable (invalid/expired) → keep scanner open and show in-modal msg.
        // Otherwise close and surface the result via the persistent toast, since
        // the in-modal message is unmounted the moment the scanner closes.
        if (!QR_SCAN_RETRYABLE.has(res.reason)) {
          showQrScanner = false;
          showE2eeToast(msg);
        }
        return;
      }
      console.debug("[e2ee] key provisioned", {
        scope: res.scope,
        scope_id: res.scope_id,
        key_id: res.key_id,
      });
      // ── Class-only tail (NOT part of the shared helper) ──
      // Shared success tail (remember key → apply on subscriber → refresh →
      // clear keyMissing/countdown), reused by the manual paste path too.
      const applied = await applyProvisionedKey(res);
      console.debug("[e2ee] applyMediaKey result", { applied });
      const okMsg = applied
        ? $_("e2ee.scan.stored")
        : $_("e2ee.scan.storedNotLive");
      e2eeMsg = okMsg;
      showQrScanner = false;
      e2eeManualMode = false;
      showE2eeToast(okMsg);
    } catch (err) {
      // The scanner already stopped the camera on detect; never leave the UI
      // silent. Cause is undecidable here → close (per the close-by-default rule).
      console.error("[e2ee] QR scan handler error", err);
      const msg = $_("e2ee.scan.error");
      e2eeMsg = msg;
      showQrScanner = false;
      showE2eeToast(msg);
    }
  }

  const sent: DcEnvelope[] = [];

  let videoEl: HTMLVideoElement | undefined;
  let audioEl: HTMLAudioElement | undefined;

  // Multi-camera: the device may publish several video tracks (one per
  // enabled camera). We collect them here and let the student switch which one
  // fills the main <video>. Only the device participant's tracks land here —
  // other students' cameras are never listed.
  interface CameraEntry {
    name: string;
    track: RemoteTrack;
    publication: RemoteTrackPublication;
  }
  let cameraTracks: CameraEntry[] = [];
  let selectedCamera: string | null = null;
  // Name of the camera whose track is currently bound to the main <video>.
  // Used by the e2e test hook to assert switching; harmless in production.
  let lastAttachedCamera: string | null = null;
  let selfPreviewEl: HTMLVideoElement | undefined;
  let confirmPreviewEl: HTMLVideoElement | undefined;
  let state: ConnectionState = "idle";
  let errorMsg: string | null = null;
  /**
   * Classified diagnosis for the CURRENT connect failure, or null when the
   * failure has no classification (e.g. a mic/camera error that already sets a
   * specific `errorMsg`). Drives the stable code line + the short status-bar
   * label; kept next to `errorMsg` so both are cleared together.
   */
  let errorInfo: ConnectErrorInfo | null = null;
  /** Expanded state of the secondary technical-detail line (support only). */
  let errorDetailsOpen = false;

  /**
   * Render a classified connect failure: user-facing message from the i18n key,
   * the raw technical string kept as SECONDARY detail. Never throws — a failure
   * to classify must still leave the user with something readable.
   */
  function setConnectError(err: unknown): void {
    let info: ConnectErrorInfo | null = null;
    try {
      info = classifyConnectError(err, {
        online: typeof navigator === "undefined" ? undefined : navigator.onLine,
        iceServerCount: lastIceServerCount,
      });
    } catch {
      info = null;
    }
    errorInfo = info;
    errorDetailsOpen = false;
    errorMsg = info
      ? $_(info.messageKey)
      : err instanceof Error
        ? err.message
        : t("error.joinFailed");
  }

  /** Clear the connect-failure state (message + classification + disclosure). */
  function clearConnectError(): void {
    errorMsg = null;
    errorInfo = null;
    errorDetailsOpen = false;
  }

  /**
   * ICE-server count from the LAST token response, fed to the classifier so it
   * can tell "the network blocked the relay we handed you" (ICE_FAILED) from
   * "no relay was configured at all" (TURN_UNREACHABLE). Count only — the TURN
   * username/credential are secrets and never leave this scope.
   */
  let lastIceServerCount = 0;

  // ── Profile image (own avatar placeholder + co-viewer tiling) ──────────────
  // The student's own avatar, shown as the stream placeholder while the device
  // video is not bound. Loaded from /me/profile-image after connect.
  let myProfileImage: string | null = null;
  // First-run prompt: shown when the student has no image yet. Re-prompts each
  // session until set (we don't persist a "skipped" flag).
  let showProfilePrompt = false;
  // True once the main device video track is actually bound, so we can hide the
  // placeholder. Driven by camera attach/detach below.
  let deviceVideoLive = false;

  // Co-viewing students: identities (emails) from LiveKit non-device
  // participants, plus a per-identity image cache so we never re-fetch (and
  // never dump base64 into a list endpoint).
  let viewerIdentities: string[] = [];
  const viewerImageCache = new Map<string, string | null>();
  // Bump to force the {#each} over the cache to re-render after async fills.
  let viewerCacheVersion = 0;

  // Room discovery front-door: before connecting we ask /rooms which rooms are
  // available to this student and keep only the ONLINE ones. One → auto-connect,
  // many → show picker, zero → friendly empty state with retry.
  type RoomPhase = "loading" | "picking" | "empty" | "connecting";
  let roomPhase: RoomPhase = "loading";
  let rooms: RoomSummary[] = [];
  let onlineRooms: RoomSummary[] = [];
  let roomsError: string | null = null;
  // Name of the room we have begun connecting to, so the offline watchdog knows
  // which bound device's heartbeat to watch during an active session.
  let connectedRoom: string | null = null;

  // Device offline watchdog: we re-poll /rooms on this cadence and treat the
  // bound device as "not online" once its last heartbeat is older than the
  // timeout. Heartbeats arrive every ~2s, so a 5s poll keeps the banner timely.
  const ROOMS_POLL_INTERVAL_MS = 5_000;
  // FIX C: cap for the exponential backoff applied after consecutive /rooms
  // failures. On every failure the next delay doubles (5s→10s→20s→30s) so a
  // flaky/offline backend can't be hammered; a single success resets to base.
  const ROOMS_POLL_MAX_INTERVAL_MS = 30_000;
  const DEVICE_OFFLINE_TIMEOUT_MS = 30_000;
  // FIX C: single-owner guard for the /rooms poll. Instead of setInterval (which
  // leaked overlapping intervals across reconnect paths), we self-schedule the
  // NEXT tick with setTimeout and tag each scheduling run with a monotonically
  // increasing generation token. stopRoomPoll() bumps the generation, so any
  // in-flight timer/await whose generation is stale becomes a no-op — only ONE
  // logical poll loop can ever be live, regardless of how many start calls race.
  let roomsPollId: ReturnType<typeof setTimeout> | null = null;
  let roomsPollGen = 0;
  let roomsPollDelay = ROOMS_POLL_INTERVAL_MS;
  let client: SubscriberClient | null = null;
  // Cleanup for the level-gated WebRTC diagnostics instrumentation (rtt/jitter/
  // fps samples + connection-state transitions). Null when not attached. Called
  // on every disconnect/leave. No-op unless the effective log level is debug+.
  let detachWebrtcDiag: (() => void) | null = null;
  let controls: StudentControls | null = null;
  let textValue = "";
  let textareaEl: HTMLTextAreaElement | undefined;

  // Camera confirm dialog: holds a local-only preview stream that is NOT
  // published until the student confirms "Send to classroom". On cancel/close
  // we stop the tracks so the LED on the device goes off immediately.
  let videoDialogOpen = false;
  let previewStream: MediaStream | null = null;
  let previewError: string | null = null;
  let previewStarting = false;
  // Local webcams the kid can choose from in the preview, and the chosen one.
  // The selection is threaded into the publish path so the picked camera is
  // what actually gets sent to the classroom.
  let videoInputs: MediaDeviceInfo[] = [];
  let selectedDeviceId: string | null = null;
  // The deviceId the preview stream is CURRENTLY showing. Tracked separately
  // from `selectedDeviceId` because the <select> uses bind:value, which mutates
  // `selectedDeviceId` BEFORE on:change fires — so it can't be used to detect
  // "did the selection actually change" inside the change handler.
  let previewDeviceId: string | null = null;

  // Classroom audio playback: muting only silences the local <audio> element.
  // The Web Audio analyser still receives samples from the remote track, so
  // the VU meter keeps moving even when muted — student can see "teacher is
  // talking" without hearing it.
  let classroomMuted = false;
  let audioCtx: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let meterSource: MediaStreamAudioSourceNode | null = null;
  let meterTrackId: string | null = null;
  let meterRaf: number | null = null;
  let meterBuffer: Uint8Array<ArrayBuffer> | null = null;
  let audioLevel = 0; // 0..1, smoothed RMS
  const METER_SEGMENTS = 12;

  // When the backend forbids invisible mode, the student is forced visible on
  // connect and the go-invisible control is hidden. Absent in the token ⇒ false.
  let invisibleForbidden = false;

  // ── Demo simulation (Test-Modus) ───────────────────────────────────────────
  // Local, backend-free classroom so the public demo always shows a polished,
  // connected experience: a room picker with sample rooms, a simulated connect,
  // two cameras, and a mirrored "what the class sees" preview.
  let demoConnected = false;
  let demoRoom: string | null = null;
  // A message the demo student "sent", shown briefly on the simulated class
  // screen so they see how a sent message appears to the class.
  let demoSentMessage = "";
  let demoMsgTimer: ReturnType<typeof setTimeout> | null = null;
  // Two cameras the demo classroom "publishes" (drives the camera switcher).
  const DEMO_CAMERAS = ["Klassenzimmer", "Tafel"];
  // Sample classrooms for the demo room picker (kept minimal + obviously demo).
  const DEMO_ROOMS: RoomSummary[] = [
    {
      name: "Beispielklasse 4a",
      visibility: "public",
      online: true,
      demo: true,
      lastSeen: Date.now(),
      onlineSince: Date.now(),
    },
    {
      name: "Beispiel-AG Musik",
      visibility: "public",
      online: true,
      demo: true,
      lastSeen: Date.now(),
      onlineSince: Date.now(),
    },
  ];

  // Bug 9: "clean view" hides ALL overlays drawn over the video stream, leaving
  // only the main <video>/<audio> (and the stream placeholder) visible.
  let cleanView = false;
  function toggleCleanView() {
    cleanView = !cleanView;
  }

  // Mobile header hamburger: collapses the secondary header actions (camera
  // select, clean-view, leave-room, logout) into a dropdown on narrow phones.
  // Desktop keeps the flat header — the menu container is `display: contents`
  // above the 640px breakpoint, so this state is inert there.
  let menuOpen = false;
  let menuEl: HTMLDivElement | null = null;
  let menuToggleEl: HTMLButtonElement | null = null;
  // Viewport width, mirrored from <svelte:window>. Above the 640px breakpoint
  // the menu container is `display: contents`, so an open menu there is stale
  // state — close it when the viewport widens past the breakpoint.
  let viewportWidth = 0;
  $: menuLabel = $_(menuOpen ? "class.menu.close" : "class.menu.open");

  function toggleMenu() {
    menuOpen = !menuOpen;
  }
  /**
   * Close the sheet. By default hands focus back to the toggle (a11y: no focus
   * loss on close); pass `restoreFocus: false` when the close was triggered by
   * the user moving focus/pointer somewhere else, so we don't steal it back.
   */
  function closeMenu(restoreFocus = true) {
    if (!menuOpen) return;
    menuOpen = false;
    if (restoreFocus) menuToggleEl?.focus();
  }
  function onMenuKeydown(ev: KeyboardEvent) {
    // Only claim Escape while the menu is open — otherwise dialogs/the text
    // composer never see it. No stopPropagation(): this handler runs on window,
    // the last hop in the bubble phase, so it can't stop anything anyway.
    if (ev.key === "Escape" && menuOpen) closeMenu();
  }
  /** Interactive targets keep the focus the user just gave them. */
  const FOCUSABLE =
    'a[href], button, input, select, textarea, summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';
  /** Outside click/tap closes the sheet (pointerdown so it beats blur/scroll). */
  function onDocPointerDown(ev: PointerEvent) {
    if (!menuOpen) return;
    const target = ev.target as Node | null;
    if (target == null) return;
    if (menuEl?.contains(target) === true) return;
    if (menuToggleEl?.contains(target) === true) return;
    // Clicking another control means the user is going there — only pull focus
    // back to the toggle when the click landed on inert page chrome.
    const el = target instanceof Element ? target : target.parentElement;
    const clickedInteractive = el?.closest(FOCUSABLE) != null;
    closeMenu(!clickedInteractive);
  }
  // Close on state changes that make the open sheet meaningless. Focus is left
  // alone here: nothing the *user* did closed it, so nothing should move.
  $: if (showRoomGate && menuOpen) menuOpen = false;
  $: if (viewportWidth > 640 && menuOpen) menuOpen = false;

  // Reactive store snapshot
  let mode: "invisible" | "visible" = "invisible";
  let raiseHandActive = false;
  let audioOn = false;
  let videoOn = false;
  let textOpen = false;

  // Single shared 1s ticker drives the clock overlay and the raise-hand countdown.
  // `now` is bumped every second; reactive blocks below derive HH:MM and the
  // remaining raise-hand seconds from it. The store has no raise-hand-start
  // timestamp, so we record `raiseHandStartedAt` locally when the hand is
  // raised (reset on lower).
  let now = Date.now();
  let tickerId: ReturnType<typeof setInterval> | null = null;
  let raiseHandStartedAt: number | null = null;
  // Wall-clock timestamp of the first successful connect. Drives the running
  // session timer shown next to the clock. Anchored once on first "connected"
  // so it keeps counting across LiveKit reconnects.
  let sessionStartedAt: number | null = null;

  function startTicker() {
    if (tickerId != null) return;
    now = Date.now();
    tickerId = setInterval(() => {
      now = Date.now();
    }, 1000);
  }

  function stopTicker() {
    if (tickerId != null) {
      clearInterval(tickerId);
      tickerId = null;
    }
  }

  // Track raise-hand start/stop to anchor the countdown. The store only exposes
  // a boolean, so we stamp the start time when it flips on and clear it on stop.
  $: {
    if (raiseHandActive && raiseHandStartedAt == null) {
      raiseHandStartedAt = Date.now();
    } else if (!raiseHandActive && raiseHandStartedAt != null) {
      raiseHandStartedAt = null;
    }
  }

  $: clock = (() => {
    const d = new Date(now);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  })();

  /** Format an elapsed duration (ms) as [hh]:[mm]:ss. */
  function formatElapsed(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const hh = String(Math.floor(total / 3600)).padStart(2, "0");
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }

  // Running session time, shown right of the wall clock once connected.
  $: sessionTime =
    sessionStartedAt != null ? formatElapsed(now - sessionStartedAt) : null;

  /** Rounded, student-friendly duration: "~Xs" / "~Xm" / "~Xh". */
  function formatRough(ms: number): string {
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60) return `~${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `~${m}m`;
    return `~${Math.round(m / 60)}h`;
  }

  /** Absolute date + time as dd.mm.yyyy HH:MM (24h). */
  function formatDateTime(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  }

  // The room whose device-offline state we surface. During a session it's the
  // connected room; in the gate it's the assigned room we most recently saw
  // online (so a student with no online room still learns when it last was).
  $: offlineRoom = (() => {
    if (connectedRoom != null) {
      return rooms.find((r) => r.name === connectedRoom) ?? null;
    }
    const seen = rooms.filter((r) => r.lastSeen != null);
    if (seen.length === 0) return null;
    return seen.reduce((a, b) => (b.lastSeen! > a.lastSeen! ? b : a));
  })();

  // Offline iff we have a last-seen stamp and it is older than the timeout.
  // `now` ticks every second, so this flips on automatically after the timeout.
  // The demo simulation never polls a real device heartbeat, so its synthetic
  // rooms would always "go offline" after the timeout — suppress it in demo.
  $: deviceOffline =
    !isDemo &&
    offlineRoom != null &&
    offlineRoom.lastSeen != null &&
    now - offlineRoom.lastSeen > DEVICE_OFFLINE_TIMEOUT_MS;

  $: offlineSinceText =
    offlineRoom?.lastSeen != null ? formatDateTime(offlineRoom.lastSeen) : "";
  $: offlineElapsedText =
    offlineRoom?.lastSeen != null ? formatRough(now - offlineRoom.lastSeen) : "";
  $: offlineWasOnlineText =
    offlineRoom?.lastSeen != null && offlineRoom?.onlineSince != null
      ? formatRough(offlineRoom.lastSeen - offlineRoom.onlineSince)
      : null;

  $: raiseHandRemaining =
    raiseHandActive && raiseHandStartedAt != null
      ? Math.max(
          0,
          Math.ceil((RAISE_HAND_TIMEOUT_MS - (now - raiseHandStartedAt)) / 1000),
        )
      : 0;

  /** Camera name for a device video publication (fallback when empty). */
  function cameraName(publication: RemoteTrackPublication): string {
    const n = publication.trackName;
    return n && n.length > 0 ? n : DEFAULT_CAMERA_LABEL;
  }

  /** True when the track belongs to the classroom DEVICE participant. */
  function isDeviceParticipant(participant?: RemoteParticipant): boolean {
    return (participant?.identity ?? "").startsWith(DEVICE_IDENTITY_PREFIX);
  }

  /** Bind the given video track to the main element. */
  function showVideoTrack(track: RemoteTrack, name?: string) {
    if (videoEl) track.attach(videoEl);
    if (name != null) lastAttachedCamera = name;
    // A device camera is now bound → hide the profile-image placeholder.
    deviceVideoLive = true;
  }

  function attachTrack(
    track: RemoteTrack,
    publication?: RemoteTrackPublication,
    participant?: RemoteParticipant,
  ) {
    if (track.kind === Track.Kind.Video) {
      // Only the device's cameras are switchable classroom cameras. Other
      // participants' video (e.g. another student) is ignored entirely.
      if (!publication || !isDeviceParticipant(participant)) return;
      const name = cameraName(publication);
      const sid = track.sid ?? publication.trackSid;
      // A muted device camera is the teacher's "temporarily hide": never list
      // it. Drop any existing entry (mirroring detachTrack's fallback) so a
      // hidden camera vanishes from the switcher instead of rendering black.
      if (publication.isMuted) {
        const muted = cameraTracks.findIndex(
          (c) => c.track.sid === sid || c.name === name,
        );
        if (muted >= 0) {
          const removed = cameraTracks[muted];
          cameraTracks = cameraTracks.filter((_, i) => i !== muted);
          if (selectedCamera === removed.name) {
            const next = cameraTracks[0] ?? null;
            if (next) {
              selectedCamera = next.name;
              showVideoTrack(next.track, next.name);
            } else {
              selectedCamera = null;
              lastAttachedCamera = null;
            }
          }
        }
        return;
      }
      const existing = cameraTracks.findIndex(
        (c) => c.track.sid === sid || c.name === name,
      );
      if (existing >= 0) {
        // Re-subscribe of a known camera: refresh the live track reference.
        cameraTracks[existing] = { name, track, publication };
        cameraTracks = cameraTracks;
      } else {
        cameraTracks = [...cameraTracks, { name, track, publication }];
      }
      // Auto-select the first camera and fill the main video.
      if (selectedCamera == null) {
        selectedCamera = name;
        showVideoTrack(track, name);
      } else if (selectedCamera === name) {
        // Currently-selected camera (re)subscribed — re-bind it.
        showVideoTrack(track, name);
      }
    } else if (track.kind === Track.Kind.Audio && audioEl) {
      track.attach(audioEl);
      // Reflect current mute state on the freshly bound element — a
      // reconnect re-attaches the track and resets `.muted` to false.
      audioEl.muted = classroomMuted;
      setupAudioMeter(track);
    }
  }

  function detachTrack(track: RemoteTrack) {
    track.detach();
    if (track.kind === Track.Kind.Video) {
      const removed = cameraTracks.find((c) => c.track === track);
      if (removed) {
        cameraTracks = cameraTracks.filter((c) => c.track !== track);
        // If the removed camera was selected, fall back to the first
        // remaining one (and attach it) or clear when none are left.
        if (selectedCamera === removed.name) {
          const next = cameraTracks[0] ?? null;
          if (next) {
            selectedCamera = next.name;
            showVideoTrack(next.track, next.name);
          } else {
            selectedCamera = null;
            lastAttachedCamera = null;
            // No device camera bound anymore → show the placeholder again.
            deviceVideoLive = false;
          }
        }
      }
      return;
    }
    if (track.kind === Track.Kind.Audio) {
      const id = track.mediaStreamTrack?.id ?? null;
      if (id && id === meterTrackId) teardownAudioMeter();
    }
  }

  /** Switch the main video to the named camera on dropdown change. */
  function onSelectCamera() {
    const entry = cameraTracks.find((c) => c.name === selectedCamera);
    if (!entry) return;
    if (videoEl) {
      // Detach whatever is currently bound, then attach the chosen track.
      for (const c of cameraTracks) c.track.detach(videoEl);
      entry.track.attach(videoEl);
    }
    lastAttachedCamera = entry.name;
  }

  /**
   * Test-only: inject stub device cameras so e2e can drive the dropdown +
   * switching without a live LiveKit room. Each name yields a minimal stub
   * track whose attach/detach are no-ops; `lastAttachedCamera` records which
   * one is bound.
   */
  function simulateCameras(names: string[]) {
    cameraTracks = names.map((name) => {
      const stub = {
        kind: Track.Kind.Video,
        sid: `stub:${name}`,
        attach() {},
        detach() {},
      } as unknown as RemoteTrack;
      const pub = {
        kind: Track.Kind.Video,
        trackName: name,
        trackSid: `stub:${name}`,
      } as unknown as RemoteTrackPublication;
      return { name, track: stub, publication: pub };
    });
    if (cameraTracks.length > 0) {
      const first = cameraTracks[0]!;
      selectedCamera = first.name;
      lastAttachedCamera = first.name;
    } else {
      selectedCamera = null;
      lastAttachedCamera = null;
    }
  }

  function toggleClassroomMute() {
    classroomMuted = !classroomMuted;
    if (audioEl) audioEl.muted = classroomMuted;
  }

  // Keep the <audio> element's mute locked to `classroomMuted` at all times.
  // The state was previously only pushed onto the element imperatively at two
  // points (track attach + toggle). LiveKit's track.attach() resets the
  // element's `.muted` back to false on every (re)subscribe/reconnect, so a
  // background re-attach could leave the element AUDIBLE while `classroomMuted`
  // was still true — the button then showed "muted" yet sound played, and the
  // first click only re-synced state (no audible change) while the second click
  // finally muted. This reactive re-asserts the mute whenever either the state
  // or the element reference changes, eliminating that one-click desync.
  $: if (audioEl) audioEl.muted = classroomMuted;

  function setupAudioMeter(track: RemoteTrack) {
    const mst = track.mediaStreamTrack;
    if (!mst) return;
    // Same track already wired — leave it alone (idempotent re-attach).
    if (meterTrackId === mst.id && analyser) return;
    teardownAudioMeter();
    try {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtx) audioCtx = new Ctx();
      // Autoplay policy may have suspended the context; resume in the
      // background. If the user hasn't gestured yet the meter stays at 0
      // until they do (which the mute button click will satisfy).
      void audioCtx.resume().catch(() => {});
      const stream = new MediaStream([mst]);
      meterSource = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      meterSource.connect(analyser);
      // Do NOT connect to destination — would double-play with <audio>.
      // Backed by a concrete ArrayBuffer (not the default ArrayBufferLike)
      // so AnalyserNode.getByteTimeDomainData's strict typing is happy.
      meterBuffer = new Uint8Array(new ArrayBuffer(analyser.fftSize));
      meterTrackId = mst.id;
      startMeterLoop();
    } catch {
      /* Web Audio unavailable — meter stays at 0, no crash. */
      teardownAudioMeter();
    }
  }

  function startMeterLoop() {
    if (meterRaf != null) return;
    const tick = () => {
      meterRaf = requestAnimationFrame(tick);
      if (!analyser || !meterBuffer) return;
      analyser.getByteTimeDomainData(meterBuffer);
      // RMS around the 128 midpoint, normalised to 0..1.
      let sum = 0;
      for (let i = 0; i < meterBuffer.length; i++) {
        const v = (meterBuffer[i]! - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / meterBuffer.length);
      // Stretch the typical 0..0.3 speech range across the full bar so
      // segments actually light up at conversational volume.
      const scaled = Math.min(1, rms * 3);
      // Light extra smoothing on top of the analyser's own.
      audioLevel = audioLevel * 0.5 + scaled * 0.5;
    };
    meterRaf = requestAnimationFrame(tick);
  }

  function teardownAudioMeter() {
    if (meterRaf != null) {
      cancelAnimationFrame(meterRaf);
      meterRaf = null;
    }
    try {
      meterSource?.disconnect();
    } catch {
      /* ignore */
    }
    meterSource = null;
    analyser = null;
    meterBuffer = null;
    meterTrackId = null;
    audioLevel = 0;
  }

  $: meterCells = (() => {
    const out: { lit: boolean; color: "green" | "yellow" | "red" }[] = [];
    for (let i = 0; i < METER_SEGMENTS; i++) {
      const threshold = (i + 1) / METER_SEGMENTS;
      const lit = audioLevel >= threshold - 1e-6;
      let color: "green" | "yellow" | "red" = "green";
      if (threshold > 0.83) color = "red";
      else if (threshold > 0.66) color = "yellow";
      out.push({ lit, color });
    }
    return out;
  })();

  // Admin "Push message" banner. The device-app republishes admin_msg over the
  // LiveKit data channel when "student" is a target; we show it as a dismissible
  // overlay (no LED concept on the student side, so no blink gate). Auto-dismisses.
  let adminMsg: string | null = null;
  let adminMsgTimer: ReturnType<typeof setTimeout> | null = null;
  const ADMIN_MSG_TTL_MS = 9000;

  function showAdminMsg(text: string) {
    adminMsg = text;
    if (adminMsgTimer) clearTimeout(adminMsgTimer);
    adminMsgTimer = setTimeout(() => {
      adminMsg = null;
      adminMsgTimer = null;
    }, ADMIN_MSG_TTL_MS);
  }

  function dismissAdminMsg() {
    adminMsg = null;
    if (adminMsgTimer) {
      clearTimeout(adminMsgTimer);
      adminMsgTimer = null;
    }
  }

  // Teacher Pause: the device broadcasts a `pause`/`resume` envelope. While
  // paused we cover the stage with a blocking overlay (high z-index) so the kid
  // never sees frozen/black video, plus the teacher's optional message.
  let paused = false;
  let pauseMessage = "";

  function onData(env: DcEnvelope) {
    // Phase 2 live flip: the kiosk broadcasts an `encryption_mode` envelope when
    // the whole-room mode changes (downgrade to plaintext / re-encrypt). The new
    // type is not part of the shared DcType union (shared layer is frozen here),
    // so it is matched by string. A clean subscriber reconnect re-fetches the
    // token, which carries the now-correct negotiated `encryption_mode`, so the
    // fresh client either enables or skips per-frame decryption to MATCH the
    // publisher. A brief blip is expected/acceptable.
    if ((env.type as string) === "encryption_mode") {
      const p = env.payload as { mode?: EncryptionMode } | undefined;
      const nextMode: EncryptionMode =
        p?.mode === "plaintext" ? "plaintext" : "encrypted";
      // Ignore a no-op signal (mode already matches our negotiated state).
      if (nextMode !== e2eeSessionMode) {
        void reconnectSubscriber();
      }
      return;
    }
    if (env.type === "pause") {
      const p = env.payload as { message?: string } | undefined;
      paused = true;
      pauseMessage = typeof p?.message === "string" ? p.message : "";
      return;
    }
    if (env.type === "resume") {
      paused = false;
      return;
    }
    if (env.type === "admin_msg") {
      const p = env.payload as
        | { text?: string; targets?: ("kiosk" | "student")[] }
        | undefined;
      // Honour targets: only show when "student" is targeted (undefined ⇒ both).
      if (
        p &&
        typeof p.text === "string" &&
        (!p.targets || p.targets.includes("student"))
      ) {
        showAdminMsg(p.text);
      }
      return;
    }
    if (!controls) return;
    if (env.type === "text_close") {
      const p = env.payload as { by: "teacher" | "student" } | undefined;
      if (p?.by === "teacher") controls.closeTextByTeacher();
    }
  }

  function applyRoomList(list: RoomSummary[]) {
    rooms = list;
    onlineRooms = list.filter((r) => r.online);
  }

  // Advance the front-door gate based on the current online rooms. No-op once a
  // room has been chosen (so a later poll can't yank a connected student around).
  function routeGate() {
    if (roomPhase === "connecting" || client != null) return;
    // Keyless-recovery auto-return: if we bounced to Login to repair the missing
    // identity, Login stashes the room name; on return drop the student straight
    // back into that room instead of the picker. One-shot (read+clear).
    if (!e2eeReturnHandled) {
      e2eeReturnHandled = true;
      const returnRoom = takePendingReturnRoom();
      if (returnRoom != null) {
        selectRoom(returnRoom);
        return;
      }
    }
    // Demo principal (Test-Modus): the demo room frequently has NO live kiosk
    // publisher, so it would never appear in `onlineRooms` and the gate would
    // land on the "no classroom online" empty state — leaving `controls` null
    // and EVERY feature button disabled. Instead we always connect the demo
    // student to the demo room (online or not). The connect may fail (room
    // offline / empty), but `start()` still instantiates `controls`, so the
    // full feature set (go visible, webcam, raise hand, audio, write message)
    // becomes usable exactly like a real student.
    if (isDemo) {
      const demoRoom =
        rooms.find((r) => r.demo === true) ?? onlineRooms[0] ?? rooms[0] ?? null;
      if (demoRoom) {
        roomPhase = "connecting";
        void start(demoRoom.name);
      }
      return;
    }
    // Always present the room picker as an explicit entry step — even when a
    // single classroom is online — so the student deliberately joins instead of
    // being dropped straight into the room on connect. (Demo principal keeps the
    // auto-connect above.)
    if (onlineRooms.length >= 1) {
      roomPhase = "picking";
    } else {
      roomPhase = "empty";
    }
  }

  async function discoverRooms() {
    roomsError = null;
    roomPhase = "loading";
    try {
      const res = await listRooms();
      applyRoomList(res.rooms);
      routeGate();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        handleSessionExpired();
        return;
      }
      roomsError =
        err instanceof Error ? err.message : "Could not load classrooms.";
      roomPhase = "empty";
    }
  }

  // Background refresh that keeps `rooms` (hence the offline watchdog) current
  // and lets the gate auto-advance when a device comes back. Transient errors
  // are swallowed so the last-known last-seen stamp keeps the banner accurate.
  async function pollRooms(): Promise<void> {
    try {
      const res = await listRooms();
      applyRoomList(res.rooms);
      routeGate();
      // Success → reset the backoff so the cadence returns to the 5s base.
      roomsPollDelay = backoffOnSuccess(ROOMS_POLL_INTERVAL_MS);
    } catch (err) {
      // Auth watchdog: a 401 means the session lapsed. Only redirect when no
      // stream is up — otherwise leave the live session untouched. Other
      // (transient) errors keep the last-known room list so the banner stays
      // accurate.
      if (err instanceof ApiError && err.status === 401 && !hasActiveStream()) {
        handleSessionExpired();
        return;
      }
      /* keep last-known room list */
      // Bounded exponential backoff on consecutive failures (cap 30s).
      roomsPollDelay = backoffOnFailure(
        roomsPollDelay,
        ROOMS_POLL_MAX_INTERVAL_MS,
      );
    }
  }

  // FIX C: self-scheduling poll loop with a single-owner generation guard. Each
  // tick polls, then re-arms the next tick using the (possibly backed-off)
  // delay — but only if its generation token is still current. stopRoomPoll()
  // bumps the generation, instantly orphaning any pending timer or in-flight
  // await so reconnect paths can never accumulate multiple loops.
  function scheduleRoomPollTick(gen: number): void {
    roomsPollId = setTimeout(() => {
      roomsPollId = null;
      if (gen !== roomsPollGen) return; // superseded by a stop/restart
      void pollRooms().finally(() => {
        if (gen !== roomsPollGen) return; // stopped while polling
        scheduleRoomPollTick(gen);
      });
    }, roomsPollDelay);
  }

  function startRoomPoll() {
    // Single-owner: tear down any existing loop first so a stale interval can't
    // coexist with a new one, then own a fresh generation.
    stopRoomPoll();
    roomsPollDelay = ROOMS_POLL_INTERVAL_MS;
    const gen = ++roomsPollGen;
    scheduleRoomPollTick(gen);
  }

  function stopRoomPoll() {
    // Bump the generation so any in-flight tick/await becomes a no-op, then
    // clear the pending timer.
    roomsPollGen += 1;
    if (roomsPollId != null) {
      clearTimeout(roomsPollId);
      roomsPollId = null;
    }
  }

  // True while a live stream is up. The auth watchdog never interrupts an
  // active session: LiveKit holds its own JWT, so a lapsed cookie must not kick
  // a kid mid-stream. Auth re-checks only matter when we're idle in the gate.
  function hasActiveStream(): boolean {
    return client != null && state === "connected";
  }

  // Auth watchdog redirect. The /rooms poll doubles as our heartbeat to the
  // backend; a 401 there means the cookie session lapsed. Without this the poll
  // swallowed the 401, the room list went stale, and the kid saw a misleading
  // "device offline" banner — only a manual reload surfaced the login form.
  // Here we redirect to login with an explanatory message instead.
  function handleSessionExpired() {
    stopRoomPoll();
    stopTicker();
    reset();
    // Set AFTER reset(): reset() clears the store (incl. error), so the message
    // must be written last for Login to render it via $session.error.
    setError(t("error.sessionExpired"));
    push("/");
  }

  function selectRoom(name: string) {
    // Demo: never hit the network — run the local classroom simulation instead.
    if (isDemo) {
      demoConnect(name);
      return;
    }
    roomPhase = "connecting";
    void start(name);
  }

  /**
   * Demo simulation: "connect" to a picked sample room WITHOUT any backend or
   * LiveKit. Presents a connected stage with two cameras and forces VISIBLE mode
   * (the demo room has no invisible mode), wiring the controls so the mirrored
   * class-view preview reacts live to raise-hand / mic / cam / message.
   */
  function demoConnect(name: string) {
    demoRoom = name;
    connectedRoom = name;
    // The demo room has no invisible mode: the student is visible from the start
    // and the go-invisible control is hidden.
    invisibleForbidden = true;
    ensureControls();
    controls?.setInvisibleForbidden(true);
    void controls?.setMode("visible", $session.user?.full_name ?? undefined);
    // Two simulated cameras drive the existing camera switcher.
    simulateCameras(DEMO_CAMERAS);
    // Present a clean, connected, encrypted-looking session.
    streamEncrypted = true;
    e2eeNotReady = false;
    if (sessionStartedAt == null) sessionStartedAt = Date.now();
    demoConnected = true;
    state = "connected";
  }

  function retry() {
    void discoverRooms();
  }

  /** Lazily fetch + cache a co-viewer's profile image by identity. */
  async function ensureViewerImage(identity: string): Promise<void> {
    if (viewerImageCache.has(identity)) return;
    // Mark as in-flight (null) so we don't double-fetch; overwrite on resolve.
    viewerImageCache.set(identity, null);
    try {
      const res = await getStudentProfileImage(identity);
      viewerImageCache.set(identity, res.image);
    } catch {
      // Forbidden / not-found / offline → keep the silhouette fallback.
      viewerImageCache.set(identity, null);
    }
    viewerCacheVersion += 1;
  }

  /** Handle a change in co-viewing students: dedupe, drop stale, prefetch. */
  function onViewersChanged(identities: string[]) {
    const myEmail = $session.user?.email ?? null;
    viewerIdentities = identities.filter((id) => id !== myEmail);
    for (const id of viewerIdentities) void ensureViewerImage(id);
  }

  // Guard so the avatar is fetched at most once per session. onMount loads it up
  // front (so the header + placeholder show the photo before a room is joined);
  // the start() call then becomes a harmless no-op.
  let myProfileImageLoaded = false;

  /** Load the student's own avatar and decide whether to prompt for one. */
  async function loadMyProfileImage() {
    if (myProfileImageLoaded) return;
    myProfileImageLoaded = true;
    try {
      const res = await getMyProfileImage();
      myProfileImage = res.image;
      showProfilePrompt = res.image == null;
    } catch {
      // Non-fatal: no placeholder image, no prompt this session. Allow a retry
      // on the next call (e.g. from start()) since this attempt failed.
      myProfileImage = null;
      myProfileImageLoaded = false;
    }
  }

  function onProfileSaved(e: CustomEvent<string>) {
    myProfileImage = e.detail;
    showProfilePrompt = false;
  }

  function onProfileSkip() {
    showProfilePrompt = false;
  }

  // Build the StudentControls store + side-effect bridge. Extracted so it can
  // be created up front (notably for the demo principal, where the LiveKit
  // connect may never succeed) and reused once a real `client` exists. The
  // bridges reference the OUTER, mutable `client`, so the same controls work
  // whether or not a room connect has completed: publish/mic/cam calls simply
  // no-op while `client` is null and start working as soon as it is set.
  function ensureControls() {
    // Keep the capability in sync even if controls already exist (token may
    // resolve after a demo-path controls creation).
    if (controls) {
      controls.setInvisibleForbidden(invisibleForbidden);
      return;
    }
    const recordingPublish = (bytes: Uint8Array, reliable: boolean) => {
      const env = decodeIncoming(bytes);
      if (env) sent.push(env);
      try {
        client?.publish(bytes, reliable);
      } catch {
        /* ignore — demo/offline room has no live data channel */
      }
    };
    controls = new StudentControls({
      publish: recordingPublish,
      setAudioPublishing: async (on) => {
        try {
          await client?.setMicrophone(on);
        } catch {
          /* ignore — no live room (demo/offline) or tests */
        }
      },
      setVideoPublishing: async (on) => {
        try {
          await client?.setCamera(on, selectedDeviceId ?? undefined);
        } catch {
          /* ignore — no live room (demo/offline) or tests */
        }
      },
    }, { invisibleForbidden });
    controls.store.subscribe((s) => {
      mode = s.mode;
      raiseHandActive = s.raiseHandActive;
      audioOn = s.audioOn;
      videoOn = s.videoOn;
      textOpen = s.textOpen;
    });
    window.__student = {
      sent,
      simulate: (env) => onData(env),
      simulateCameras,
      activeCamera: () => lastAttachedCamera,
    };
  }

  async function start(room: string) {
    clearConnectError();
    e2eeBlocked = null;
    e2eeKeylessReported = false;
    // FIX B: forced status is unknown until THIS join's token lands. Reset the
    // tri-state so the plaintext-fallback button can't render against a stale
    // (previous-room) forced verdict during the connecting window.
    e2eeForcedKnown = false;
    connectedRoom = room;
    state = "connecting";
    void loadMyProfileImage();
    // Demo principal: create the controls up front so EVERY feature button is
    // usable even if the demo room is offline/empty and the token mint or the
    // LiveKit connect below fails. For a real student we still create controls
    // here; the connect simply lights up the live data path afterwards.
    if (isDemo) ensureControls();
    try {
      // Send the client's runtime E2EE capability + the persisted opt-out so the
      // backend negotiates the whole-session state (and BLOCKS the join when a
      // forcing device meets an incapable client). Only meaningful when the flag
      // is on; harmless extra fields otherwise.
      const tok = await fetchToken(
        e2eeEnabled
          ? {
              room,
              e2ee_supported: e2eeClientSupports,
              e2ee_opt_out: e2eeOptOut,
            }
          : { room },
      );
      // Backend may forbid invisible mode for this session. Record it and push
      // the capability onto the controls instance (created up front for demo,
      // or below for a real student).
      invisibleForbidden = tok.invisible_forbidden === true;
      controls?.setInvisibleForbidden(invisibleForbidden);
      // P4: when QR exchange is forced, no key is auto-sealed — the student must
      // scan the device QR. Recorded so the connect path shows "scan required"
      // instead of waiting for a silent auto-load that will never arrive.
      forceE2eeQr = tok.force_e2ee_qr === true;
      // Negotiated whole-session E2EE state from the token. Drives the overlay
      // lock + whether the opt-out toggle is locked.
      if (tok.e2ee_session) {
        e2eeSessionActive = tok.e2ee_session.active;
        e2eeSessionForced = tok.e2ee_session.forced;
        // Phase 6: remember the active device key_id for the rotation/stale check.
        e2eeSessionKeyId = tok.e2ee_session.key_id ?? null;
        // Phase 2: the negotiated whole-room mode. Absent ⇒ "encrypted".
        e2eeSessionMode = tok.e2ee_session.encryption_mode ?? "encrypted";
      } else {
        e2eeSessionActive = false;
        e2eeSessionForced = false;
        e2eeSessionKeyId = null;
        e2eeSessionMode = "encrypted";
      }
      client = new SubscriberClient(
        {
          onState: (s) => {
            state = s;
            // Anchor the session timer on the first successful connect and
            // keep it running across later reconnects.
            if (s === "connected" && sessionStartedAt == null) {
              sessionStartedAt = Date.now();
            }
            if (s === "connected") {
              // Forbidden-invisible session: force the student VISIBLE now that
              // the data channel is live so the presence broadcast goes out.
              if (invisibleForbidden) {
                controls?.setInvisibleForbidden(true);
                void controls?.setMode("visible", $session.user?.full_name ?? undefined);
              }
              // Reliability: re-evaluate the readiness gate now that we are in a
              // room and know the negotiated forced/mode state. When the session
              // wants E2EE but we hold NO usable identity and recovery is not
              // pending, a QR scan is futile (`noIdentity`). In a NON-forced room
              // auto-fall back to plaintext so the student can still watch; in a
              // forced room keep the existing block/QR-required behavior.
              if (
                e2eeEnabled &&
                (e2eeSessionActive || e2eeSessionForced) &&
                e2eeSessionMode === "encrypted" &&
                !hasIdentity() &&
                !e2eeRecoveryNeeded
              ) {
                e2eeNotReady = true;
                // FIX B: maybeAutoDowngradeNoIdentity only fires for NON-forced
                // rooms; a FORCED room (or one still pre-downgrade) would deadlock
                // on the eternal notReady banner. Start the bounded auto-recovery
                // so the student heals (refetch+apply) or bounces (fresh token)
                // and is never stranded — NEVER downgrading a forced room.
                maybeAutoDowngradeNoIdentity();
                maybeStartNotReadyRecovery();
              }
              // The subscriber auto-installs the device-scoped key on connect.
              // Enforce the NEGOTIATED desired state: when the session is not
              // active (student opted out, device not forcing), turn per-frame
              // decryption back OFF so the overlay + media match the opt-out.
              // Forced/active sessions stay encrypted. Best-effort.
              //
              // Phase 2: in a downgraded (plaintext) room the publisher sends
              // PLAINTEXT frames, so a capable student MUST keep per-frame
              // decryption OFF — enabling it would garble the (unencrypted) video.
              if (e2eeSessionMode === "plaintext") {
                void client?.setE2EEEnabled(false).then(refreshEncryptedState);
              } else if (!e2eeSessionActive) {
                void client?.setE2EEEnabled(false).then(refreshEncryptedState);
              } else if (forceE2eeQr) {
                // P4: in-person QR is mandated — keys are NOT auto-sealed, so
                // skip the silent late-key retry and tell the student to scan.
                refreshEncryptedState();
                if (getMediaKeyId(DEVICE_KEY_SCOPE, DEVICE_KEY_SCOPE_ID) == null) {
                  e2eeMsg = $_("e2ee.qr.scanRequired");
                }
              } else {
                refreshEncryptedState();
                // P3: keep re-pulling keys briefly so an auto-sealed key that
                // lands AFTER connect installs silently (no scan needed).
                scheduleLateKeyRetry();
              }
            }
          },
          onTrack: (track, publication, participant) =>
            attachTrack(track, publication, participant),
          onTrackUnsubscribed: (track) => detachTrack(track),
          onViewersChanged,
          // The subscriber already classified the failure (it knows the ICE
          // server count). Prefer its verdict; fall back to classifying here so
          // a caller that omits `info` still gets a specific message rather
          // than a stringified LiveKit internal.
          onError: (e, info) => {
            if (info) {
              errorInfo = info;
              errorDetailsOpen = false;
              errorMsg = $_(info.messageKey);
            } else {
              setConnectError(e);
            }
          },
          onData,
          // Robustness: pause is ephemeral signaling. If the teacher kiosk
          // restarts while paused it republishes media but never sends a
          // `resume`, so a stale Wait overlay would persist until manual reload.
          // The device leaving the room is the authoritative un-stick signal.
          onDevicePresence: (present) => {
            if (!present) {
              paused = false;
              pauseMessage = "";
            }
          },
          // Keyless-recovery UX: the subscriber raised an EncryptionError (e.g.
          // missing key idx 0). This RoomEvent can fire PER FRAME, so the handler
          // must stay cheap + idempotent: only flip the calm "waiting for key"
          // flag when E2EE is on and we genuinely hold no usable key, then kick
          // the existing late-key retry + countdown if not already running.
          onEncryptionError: () => {
            if (!e2eeEnabled) return;
            if (client?.hasMediaKey() === true) return; // key present → ignore
            if (e2eeSessionMode === "plaintext") return; // downgraded → no key needed
            if (!e2eeKeyMissing) {
              e2eeKeyMissing = true;
              // FIX C observability: first live "No key set found" while we hold
              // no media key — record it once with the expected key_id.
              logKeylessBreadcrumb("encryption error (no key set)");
              startKeyWaitCountdown();
              scheduleLateKeyRetry();
            }
          },
        },
        {
          iceServers: tok.iceServers,
          // Build the KeyProvider whenever E2EE is on AND the client is capable.
          // Whether per-frame decryption is actually enabled is then driven by
          // the negotiated session state (enforced in the `connected` handler).
          e2ee: e2eeEnabled && e2eeClientSupports,
        },
      );
      // COUNT only (never the TURN credentials) — lets the error classifier
      // distinguish a blocked relay from no relay at all.
      lastIceServerCount = tok.iceServers?.length ?? 0;
      // Create controls now (if not already created up front for demo). The
      // bridges reference the outer `client` we just assigned, so publish /
      // mic / cam start working immediately for a real student.
      ensureControls();
      await client.connect(tok.url, tok.token);
      // Level-gated rich WebRTC diagnostics: instruments the LiveKit room for
      // connection-state transitions + periodic rtt/jitter/fps stats. No-op
      // unless the effective log level is debug+. Defensive: never let
      // diagnostics wiring break a successful join.
      try {
        detachWebrtcDiag?.();
        detachWebrtcDiag = attachWebrtcDiagnostics(client.room, {
          label: "student",
        });
        logDiagBreadcrumb("join room", { room });
      } catch {
        /* ignore */
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        handleSessionExpired();
        return;
      }
      // Blocked join: the device forces E2EE but this client can't do it. Show
      // the remediation screen instead of a generic error.
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        err.body &&
        typeof err.body === "object" &&
        (err.body as { error?: string }).error === "e2ee_required"
      ) {
        const rem = (err.body as E2eeRequiredResponse).remediation;
        e2eeBlocked = rem ?? null;
        return;
      }
      // Demo principal: the demo room may be offline/empty (no kiosk publisher),
      // so the token mint (room_offline) or the LiveKit connect can fail. That
      // is EXPECTED in Test-Modus — we deliberately don't surface a join error
      // and don't tear anything down. `controls` was already instantiated above
      // (before the connect await), so all feature buttons stay usable; the
      // demo placeholder tile stands in for the missing live video.
      if (isDemo) {
        state = "disconnected";
        return;
      }
      state = "error";
      // Classify instead of surfacing LiveKit's internal string ("could not
      // establish pc connection") or a bare backend message: the student gets a
      // specific, actionable sentence plus a stable code to quote to support,
      // with the raw text kept as a secondary, expandable detail line.
      setConnectError(err);
    }
  }

  // Phase 2: guard so overlapping mode-change envelopes don't stack reconnects.
  let reconnecting = false;

  /**
   * Phase 2 live flip: cleanly reconnect the subscriber so its FRESH
   * `/livekit/token` re-negotiates the now-correct `encryption_mode`. Reuses the
   * full `start()` path (token re-fetch + new SubscriberClient + connect), which
   * is the mechanism that already exists for joining a room. A ~1–2s blip is
   * accepted per the locked decision (auto-reconnect, never in-place toggling).
   */
  async function reconnectSubscriber(): Promise<void> {
    if (reconnecting) return;
    const room = connectedRoom;
    if (room == null) return;
    reconnecting = true;
    // Clear any stale connect error so the reconnect doesn't carry a prior
    // "failed to fetch" into the fresh session (consistent with start()).
    clearConnectError();
    // Clear the pending late-key retry / keyless guard from the prior session so
    // the fresh connect re-arms them under the new negotiated state.
    if (e2eeRetryTimer !== null) {
      clearInterval(e2eeRetryTimer);
      e2eeRetryTimer = null;
    }
    e2eeKeylessReported = false;
    try {
      await client?.disconnect();
    } catch {
      /* ignore */
    }
    client = null;
    try {
      await start(room);
    } finally {
      reconnecting = false;
    }
  }

  /** Open the profile page, passing the currently-connected room (for teacher
   * contact lookup) when known. */
  function openProfile() {
    const room = connectedRoom ? `?room=${encodeURIComponent(connectedRoom)}` : "";
    push(`/profile${room}`);
  }

  async function doLogout() {
    try {
      logDiagBreadcrumb("leave", { reason: "logout" });
      detachWebrtcDiag?.();
      detachWebrtcDiag = null;
    } catch {
      /* ignore */
    }
    try {
      await client?.disconnect();
    } catch {
      /* ignore */
    }
    try {
      await logout();
    } catch {
      /* ignore */
    }
    reset();
    push("/");
  }

  // Bug 4: leave the current room WITHOUT logging out. Mirrors doLogout's
  // teardown but keeps the session, resets stream state to the pre-join state,
  // and re-opens the room selector so the student can pick another room.
  async function leaveRoom() {
    try {
      logDiagBreadcrumb("leave", { reason: "leaveRoom" });
      detachWebrtcDiag?.();
      detachWebrtcDiag = null;
    } catch {
      /* ignore */
    }
    try {
      await client?.disconnect();
    } catch {
      /* ignore */
    }
    client = null;
    connectedRoom = null;
    // Reset stream-related state back to the pre-join state.
    state = "idle";
    deviceVideoLive = false;
    cameraTracks = [];
    selectedCamera = null;
    lastAttachedCamera = null;
    sessionStartedAt = null;
    paused = false;
    pauseMessage = "";
    teardownAudioMeter();
    // Reset visibility back to the default so the next room starts invisible.
    mode = "invisible";
    // Clear any pending E2EE late-key retry from the previous session.
    if (e2eeRetryTimer !== null) {
      clearInterval(e2eeRetryTimer);
      e2eeRetryTimer = null;
    }
    // FIX B: tear down the notReady bounded-recovery countdown + reset its state
    // so the next room starts clean (no stale terminal flag / countdown).
    stopNotReadyCountdown();
    e2eeNotReady = false;
    e2eeNotReadyAttempts = 0;
    e2eeNotReadyTerminal = false;
    // Re-open the room selector (sets roomPhase to picking) and keep polling.
    void discoverRooms();
    startRoomPoll();
  }

  async function toggleMode() {
    if (!controls) return;
    // Forbidden students can only ever be visible — never toggle back.
    if (invisibleForbidden) {
      await controls.setMode("visible", $session.user?.full_name ?? undefined);
      return;
    }
    await controls.setMode(
      mode === "invisible" ? "visible" : "invisible",
      $session.user?.full_name ?? undefined,
    );
  }

  function doRaiseHand() {
    if (!controls) return;
    if (raiseHandActive) controls.lowerHand();
    else controls.raiseHand($session.user?.full_name ?? undefined);
  }

  async function toggleAudio() {
    if (!controls) return;
    try {
      await controls.setAudio(!audioOn);
    } catch (err) {
      errorMsg = t("error.mic", {
        reason: err instanceof Error ? err.message : t("error.blocked"),
      });
    }
  }

  async function toggleVideo() {
    if (!controls) return;
    if (videoOn) {
      // Turning OFF goes straight through — no confirmation needed.
      try {
        await controls.setVideo(false);
      } catch (err) {
        errorMsg = t("error.cam", {
          reason: err instanceof Error ? err.message : t("error.blocked"),
        });
      }
      // LiveKit auto-detaches on unpublish, but the element keeps the last
      // frame as a poster until the next attach. Reload the element so the
      // pip goes black immediately.
      try {
        selfPreviewEl?.pause();
        if (selfPreviewEl) selfPreviewEl.srcObject = null;
      } catch {
        /* ignore */
      }
      return;
    }
    await openVideoDialog();
  }

  async function openVideoDialog() {
    previewError = null;
    previewStarting = true;
    videoDialogOpen = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      previewStream = stream;
      selectedDeviceId =
        stream.getVideoTracks()[0]?.getSettings().deviceId ?? null;
      // Element is mounted by the {#if videoDialogOpen} block; wait one tick
      // so the bind:this lands before we attach the stream.
      previewDeviceId = selectedDeviceId;
      await Promise.resolve();
      if (confirmPreviewEl) confirmPreviewEl.srcObject = stream;
      // Labels are only populated after the first getUserMedia grant.
      await refreshVideoInputs();
      logDiagBreadcrumb("device selected", { deviceId: selectedDeviceId });
    } catch (err) {
      previewError = err instanceof Error ? err.message : t("error.cameraBlocked");
      logDiagBreadcrumb("permission denied", {
        what: "camera",
        name: err instanceof Error ? err.name : undefined,
      });
    } finally {
      previewStarting = false;
    }
  }

  // List the local video-input devices (webcams) for the preview selector.
  async function refreshVideoInputs() {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      videoInputs = devs.filter((d) => d.kind === "videoinput");
    } catch {
      videoInputs = [];
    }
  }

  // Switch the preview to a different webcam without publishing anything yet.
  async function selectPreviewCamera(deviceId: string) {
    // Compare against the device the preview is ACTUALLY showing, not
    // `selectedDeviceId` — bind:value has already updated the latter, so
    // comparing to it here would always short-circuit and freeze the preview.
    if (!deviceId || deviceId === previewDeviceId) return;
    selectedDeviceId = deviceId;
    previewError = null;
    previewStarting = true;
    // Release the current camera before grabbing the new one — some stacks
    // reject a second getUserMedia while the first stream is still live.
    stopPreviewStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });
      previewStream = stream;
      previewDeviceId = deviceId;
      await Promise.resolve();
      if (confirmPreviewEl) confirmPreviewEl.srcObject = stream;
    } catch (err) {
      previewError = err instanceof Error ? err.message : t("error.cameraBlocked");
    } finally {
      previewStarting = false;
    }
  }

  function stopPreviewStream() {
    if (previewStream) {
      for (const t of previewStream.getTracks()) t.stop();
      previewStream = null;
    }
    if (confirmPreviewEl) {
      try {
        confirmPreviewEl.srcObject = null;
      } catch {
        /* ignore */
      }
    }
  }

  function cancelVideoDialog() {
    stopPreviewStream();
    videoDialogOpen = false;
    previewError = null;
    videoInputs = [];
    selectedDeviceId = null;
    previewDeviceId = null;
  }

  async function confirmVideoDialog() {
    if (!controls) return;
    // Release the preview's camera handle BEFORE LiveKit grabs it again —
    // some Android stacks reject a second getUserMedia while the first
    // MediaStream is still live.
    stopPreviewStream();
    videoDialogOpen = false;
    try {
      await controls.setVideo(true);
      attachSelfPreview();
    } catch (err) {
      errorMsg = t("error.cam", {
        reason: err instanceof Error ? err.message : t("error.blocked"),
      });
    }
  }

  function attachSelfPreview() {
    if (!client || !selfPreviewEl) return;
    const camPub = client.room.localParticipant
      .getTrackPublications()
      .find((p) => p.kind === Track.Kind.Video);
    const camTrack = camPub?.track;
    if (camTrack) camTrack.attach(selfPreviewEl);
  }

  function openText() {
    controls?.openText();
    textValue = "";
    setTimeout(() => textareaEl?.focus(), 0);
  }

  function sendText() {
    if (!controls) return;
    const t = textValue.trim();
    if (!t) return;
    controls.sendText(t, $session.user?.full_name ?? undefined);
    textValue = "";
    // Demo: surface the message on the simulated class screen for a few seconds
    // so the student sees how their message appears to the class.
    if (isDemo) {
      demoSentMessage = t;
      if (demoMsgTimer) clearTimeout(demoMsgTimer);
      demoMsgTimer = setTimeout(() => {
        demoSentMessage = "";
        demoMsgTimer = null;
      }, 6000);
    }
  }

  function closeText() {
    controls?.closeTextByStudent();
  }

  // --- Visual-viewport height ------------------------------------------------
  // `100dvh` covers the collapsing URL bar on most browsers, but iOS Safari can
  // still leave a strip of background below the bottom toolbar (dvh is the
  // *largest* stable dynamic height, not the currently visible one). Mirror the
  // live visual viewport into `--app-height`; `.class` prefers it over dvh.
  //
  // The property is set on the `.class` element (not documentElement) so it
  // stays scoped to this route and can't leak into others.
  let vvCleanup: (() => void) | null = null;
  let classEl: HTMLElement | null = null;

  function syncAppHeight() {
    const vv = window.visualViewport;
    if (!vv || !classEl) return;
    // Software keyboard also shrinks the visual viewport. Ignore those updates
    // (text mode) — otherwise the whole class view collapses while typing.
    // focusout re-runs this once the keyboard is gone, so a rotate-while-typing
    // can't leave a stale height behind.
    const active = document.activeElement;
    const typing =
      active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
    if (typing) return;
    classEl.style.setProperty("--app-height", `${vv.height}px`);
  }

  /** Re-measure after the keyboard closes — the viewport needs a frame to settle. */
  function resyncAppHeightSoon() {
    requestAnimationFrame(() => {
      syncAppHeight();
    });
  }

  function startAppHeightSync() {
    const vv = window.visualViewport;
    if (!vv) return;
    syncAppHeight();
    vv.addEventListener("resize", syncAppHeight);
    vv.addEventListener("scroll", syncAppHeight);
    document.addEventListener("focusout", resyncAppHeightSoon);
    vvCleanup = () => {
      vv.removeEventListener("resize", syncAppHeight);
      vv.removeEventListener("scroll", syncAppHeight);
      document.removeEventListener("focusout", resyncAppHeightSoon);
      classEl?.style.removeProperty("--app-height");
    };
  }

  // `.class` is behind the must-change-password guard, so the element can
  // appear after mount. Measure as soon as it exists.
  $: if (classEl) syncAppHeight();

  onMount(() => {
    startAppHeightSync();
    document.addEventListener("pointerdown", onDocPointerDown, true);
    startTicker();
    void initE2ee();
    // Bug 2: load the own avatar up front so the header + stream placeholder
    // show the photo before/independently of joining a room (idempotent —
    // start() may also call it, but the guard prevents a re-fetch).
    void loadMyProfileImage();
    // Demo: skip real room discovery + polling entirely. Show a simulated room
    // picker with sample classrooms; selectRoom() runs the local simulation.
    if ($session.user?.demo === true) {
      onlineRooms = DEMO_ROOMS;
      rooms = DEMO_ROOMS;
      roomPhase = "picking";
      return;
    }
    startRoomPoll();
    if (!$session.user) {
      // Wait one tick for /auth/me bootstrap; if still no user, send to login.
      setTimeout(() => {
        if (!$session.user) {
          push("/");
        } else {
          void discoverRooms();
        }
      }, 50);
      return;
    }
    void discoverRooms();
  });

  onDestroy(async () => {
    vvCleanup?.();
    vvCleanup = null;
    document.removeEventListener("pointerdown", onDocPointerDown, true);
    stopTicker();
    stopRoomPoll();
    stopPreviewStream();
    teardownAudioMeter();
    if (adminMsgTimer) clearTimeout(adminMsgTimer);
    if (demoMsgTimer) clearTimeout(demoMsgTimer);
    if (e2eeRetryTimer !== null) clearInterval(e2eeRetryTimer);
    stopKeyWaitCountdown();
    stopNotReadyCountdown();
    e2eeRepairAttempts = 0;
    try {
      await audioCtx?.close();
    } catch {
      /* ignore */
    }
    audioCtx = null;
    try {
      detachWebrtcDiag?.();
      detachWebrtcDiag = null;
    } catch {
      /* ignore */
    }
    try {
      await client?.disconnect();
    } catch {
      /* ignore */
    }
  });

  // A bare "Fehler" tells the student nothing. When the failure was classified,
  // show the short category label ("Netzwerk blockiert", "Server-Problem", …)
  // instead; only an unclassified error falls back to the generic word.
  $: indicator =
    effectiveState === "connected"
      ? $_("status.connected")
      : effectiveState === "connecting" || effectiveState === "idle"
        ? $_("status.connecting")
        : effectiveState === "disconnected"
          ? $_("status.disconnected")
          : errorInfo
            ? $_(errorInfo.shortKey)
            : $_("status.error");

  $: controlsEnabled = controls != null && mode === "visible";

  // Demo principal (Test-Modus): drives the persistent badge + the placeholder
  // stream shown when no real classroom publisher is present.
  $: isDemo = $session.user?.demo === true;

  // Demo simulation: the deployed demo room usually has no live kiosk publisher,
  // so a real LiveKit connect would land on "disconnected" with an empty stage.
  // Instead we run a fully LOCAL simulation: pick a (fake) room, then present a
  // connected stream, two cameras, and a mirrored "what the class sees" preview
  // driven entirely by the student's own control state. No backend, no LiveKit.
  $: effectiveState = isDemo && demoConnected ? "connected" : state;

  // Forced first-login password change: when the backend marks the account
  // `must_change_password`, we block the entire classroom UI behind the
  // ChangePassword guard until the student sets a new password. The demo
  // principal is seeded with the flag off, so demo is never gated here.
  $: mustChangePassword = $session.user?.must_change_password === true;

  // The simulated "class view" replaces the stage once a demo room is picked.
  $: showDemoClassScreen = isDemo && demoConnected;

  // Show the room front-door (loading / picker / empty) until a room has been
  // chosen and we've begun connecting. `client` is created inside start().
  // In demo mode the gate shows a (simulated) room picker until the student
  // picks a sample classroom; afterwards the simulated connected stage renders.
  $: showRoomGate = isDemo
    ? !demoConnected
    : roomPhase !== "connecting" && client == null;

  // Bug 3 + Bug 9: stream overlays (controls, HUDs, self-preview, etc.) render
  // only when actually in a room (NOT the room gate) AND clean view is off.
  $: showOverlays = !showRoomGate && !cleanView;
</script>

<svelte:window on:keydown={onMenuKeydown} bind:innerWidth={viewportWidth} />

{#if mustChangePassword}
  <!-- Forced first-login change: blocks the classroom until the password is
       changed. On success the session flag clears and this {#if} releases,
       rendering the normal class view below. -->
  <ChangePassword />
{:else}
<section class="class" bind:this={classEl}>
  <header>
    <button
      class="who"
      data-testid="who"
      on:click={openProfile}
      title={$_("profilePage.open")}
      aria-label={$_("profilePage.open")}
    >
      <Avatar
        image={myProfileImage}
        name={$session.user?.display_name ?? $session.user?.email ?? ""}
        size={28}
      />
      <span class="who-name">
        {$session.user?.display_name ?? $session.user?.email ?? ""}
      </span>
    </button>
    {#if isDemo}
      <span
        class="test-mode-badge"
        data-testid="test-mode-badge"
        title={$_("testMode.tip")}
        aria-label={$_("testMode.tip")}
      >
        {$_("testMode.badge")}
      </span>
    {/if}
    <div class="status" data-testid="status" data-state={effectiveState}>
      <span class="dot" class:on={effectiveState === "connected"}></span>
      {indicator}
    </div>
    <!-- Secondary actions. Desktop: `display: contents` so they stay direct
         flex items of the header (layout identical to before). Mobile
         (<=640px): collapsed into the hamburger dropdown below. -->
    <div
      class="header-actions"
      class:open={menuOpen}
      id="header-actions-menu"
      data-testid="header-actions"
      bind:this={menuEl}
    >
      {#if cameraTracks.length > 1}
        <select
          class="camera-select"
          data-testid="camera-select"
          aria-label={$_("camera.label")}
          bind:value={selectedCamera}
          on:change={() => {
            onSelectCamera();
            closeMenu();
          }}
        >
          {#each cameraTracks as cam (cam.track.sid ?? cam.name)}
            <option value={cam.name}>{cam.name}</option>
          {/each}
        </select>
      {/if}
      {#if !showRoomGate}
        <button
          type="button"
          class="clean-view-btn"
          data-testid="clean-view-toggle"
          on:click={() => {
            toggleCleanView();
            closeMenu();
          }}
          aria-pressed={cleanView}
          title={cleanView ? $_("cleanView.show.tip") : $_("cleanView.hide.tip")}
          aria-label={cleanView ? $_("cleanView.show.tip") : $_("cleanView.hide.tip")}
        >
          <Icon name="visibility" on={!cleanView} />
        </button>
      {/if}
      {#if client != null}
        <button
          type="button"
          on:click={() => {
            closeMenu();
            void leaveRoom();
          }}
          data-testid="leave-room"
        >
          {$_("class.leaveRoom")}
        </button>
      {/if}
      <button
        on:click={() => {
          closeMenu();
          void doLogout();
        }}
        data-testid="logout">{$_("class.logout")}</button
      >
    </div>
    <!-- Hamburger: mobile only (hidden >640px via CSS), toggles .header-actions.
         Plain disclosure (aria-expanded + aria-controls), NOT a menu: the
         revealed panel contains a native <select>, which is invalid content
         for role="menu"/"menuitem". -->
    <button
      type="button"
      class="menu-toggle"
      data-testid="header-menu-toggle"
      aria-label={menuLabel}
      title={menuLabel}
      aria-expanded={menuOpen}
      aria-controls="header-actions-menu"
      on:click={toggleMenu}
      bind:this={menuToggleEl}
    >
      <span class="menu-bars" aria-hidden="true"></span>
    </button>
  </header>

  {#if showRoomGate}
    <div class="room-gate" data-testid="room-gate">
      <div class="room-gate-card">
        {#if roomPhase === "loading"}
          <p class="gate-msg" data-testid="rooms-loading">{$_("room.finding")}</p>
        {:else if roomPhase === "picking"}
          <div class="room-picker" data-testid="room-picker">
            <h2>{$_("room.pick")}</h2>
            <div class="room-list">
              {#each onlineRooms as room (room.name)}
                <button
                  type="button"
                  class="room-option"
                  data-testid={`room-option-${room.name}`}
                  on:click={() => selectRoom(room.name)}
                >
                  <span class="room-name">{room.name}</span>
                  <span class="room-visibility" data-visibility={room.visibility}>
                    {room.visibility === "public"
                      ? $_("room.visibility.public")
                      : $_("room.visibility.restricted")}
                  </span>
                </button>
              {/each}
            </div>
          </div>
        {:else}
          <div class="no-rooms" data-testid="no-rooms">
            <p>{$_("room.none")}</p>
            {#if roomsError}
              <p class="error-inline" data-testid="rooms-error">{roomsError}</p>
            {/if}
            <button type="button" data-testid="rooms-retry" on:click={retry}>
              {$_("room.retry")}
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if e2eeBlocked}
    <div class="e2ee-blocked" data-testid="e2ee-blocked" role="alertdialog">
      <div class="e2ee-blocked-card">
        <div class="e2ee-blocked-icon" aria-hidden="true">🔒</div>
        <h2 class="e2ee-blocked-title">
          {blockedTitle(e2eeBlocked)}
        </h2>
        <p class="e2ee-blocked-detail">
          {blockedDetail(e2eeBlocked)}
        </p>
        <ul class="e2ee-blocked-steps" data-testid="e2ee-blocked-steps">
          <li>{$_("e2ee.blocked.step.chrome")}</li>
          <li>{$_("e2ee.blocked.step.ios")}</li>
          <li>{$_("e2ee.blocked.step.update")}</li>
        </ul>
        <button
          type="button"
          class="e2ee-blocked-retry"
          data-testid="e2ee-blocked-retry"
          on:click={() => {
            e2eeBlocked = null;
            void discoverRooms();
          }}
        >
          {$_("e2ee.blocked.retry")}
        </button>
      </div>
    </div>
  {/if}

  {#if paused}
    <div class="paused-overlay" data-testid="paused-overlay" role="alert">
      <div class="paused-card">
        <div class="paused-icon" aria-hidden="true">⏸</div>
        <h2 class="paused-title">{$_("paused.title")}</h2>
        {#if pauseMessage.trim().length > 0}
          <p class="paused-message" data-testid="paused-message">{pauseMessage}</p>
        {/if}
      </div>
    </div>
  {/if}

  {#if e2eeEnabled && e2eeRecoveryNeeded}
    <!-- Phase 1 recovery routing: login could not unlock the stored identity but
         a recovery blob exists. A QR scan can't help without an in-memory
         identity, so prompt for the RECOVERY CODE instead of the rekey/QR
         overlay. -->
    <div class="e2ee-rekey" data-testid="e2ee-recovery" role="alertdialog">
      <div class="e2ee-rekey-card">
        <div class="e2ee-rekey-icon" aria-hidden="true">🔐</div>
        <h2 class="e2ee-rekey-title">{$_("e2ee.recovery.title")}</h2>
        <p class="e2ee-rekey-detail">{$_("e2ee.recovery.detail")}</p>
        <input
          class="e2ee-recovery-input"
          data-testid="e2ee-recovery-input"
          type="text"
          autocomplete="off"
          bind:value={e2eeRecoveryCode}
          placeholder={$_("e2ee.recovery.placeholder")}
          aria-label={$_("e2ee.recovery.placeholder")}
        />
        <div class="e2ee-rekey-actions">
          <button
            type="button"
            class="e2ee-rekey-scan"
            data-testid="e2ee-recovery-submit"
            disabled={e2eeRecoveryBusy || e2eeRecoveryCode.trim().length === 0}
            on:click={submitRecoveryCode}
          >
            {$_("e2ee.recovery.submit")}
          </button>
          {#if !e2eeForced}
            <button
              type="button"
              class="e2ee-rekey-help"
              data-testid="e2ee-recovery-plaintext"
              on:click={continueUnencrypted}
            >
              {$_("e2ee.compat.continuePlain")}
            </button>
          {/if}
        </div>
        {#if e2eeRecoveryMsg}
          <p class="e2ee-modal-msg" data-testid="e2ee-recovery-msg">
            {e2eeRecoveryMsg}
          </p>
        {/if}
      </div>
    </div>
  {/if}

  {#if e2eeEnabled && e2eeNotReady && !e2eeRecoveryNeeded && e2eeSessionMode !== "plaintext" && !isDemo && state === "connected"}
    <!-- FIX B: the notReady state now has a BOUNDED auto-recovery (countdown →
         refetch+apply → fresh-token rejoin, capped). The banner reflects it:
           - recovering  → "noch nicht bereit" + live countdown (no dead button)
           - exhausted   → clear TERMINAL message
         The plaintext escape ("Unverschlüsselt fortfahren") renders ONLY when
         forced status is KNOWN and the room is NOT forced (tri-state gate); in a
         forced/unknown room we show an actionable "Schlüssel erneuern / Neu
         verbinden" instead — never a downgrade, never a dead no-op. The `×` is
         hidden when there is no valid manual escape (forced + still keyless);
         otherwise it triggers real recovery via on:dismiss. -->
    <DismissibleBanner
      tone="warning"
      icon={e2eeNotReadyTerminal ? "⚠️" : "⏳"}
      title={$_("e2ee.notReady.title")}
      detail={e2eeNotReadyTerminal
        ? $_("e2ee.notReady.terminal")
        : e2eeNotReadySeconds > 0
          ? $_("e2ee.notReady.recovering", { seconds: e2eeNotReadySeconds })
          : $_("e2ee.notReady.detail")}
      testid="e2ee-notready"
      closeLabel={$_("e2ee.settings.close")}
      dismissible={e2eeForcedKnown && !e2eeForced}
      collapseOnDismiss={false}
      on:dismiss={() => {
        if (e2eeForcedKnown && !e2eeForced) continueUnencrypted();
      }}
    >
      {#if e2eeForcedKnown && !e2eeForced}
        <button
          type="button"
          class="e2ee-rekey-help"
          data-testid="e2ee-notready-plaintext"
          on:click={continueUnencrypted}
        >
          {$_("e2ee.compat.continuePlain")}
        </button>
      {:else}
        <!-- Forced or forced-status-unknown: never offer plaintext. Give the
             student a working action that runs the same recovery path. -->
        <button
          type="button"
          class="e2ee-rekey-help"
          data-testid="e2ee-notready-reprovision"
          on:click={reprovisionAndReconnect}
        >
          {$_("e2ee.notReady.reprovision")}
        </button>
      {/if}
    </DismissibleBanner>
  {/if}

  {#if e2eeEnabled && e2eeSessionMode === "plaintext"}
    <!-- Phase 3: clear warning that the WHOLE room downgraded to plaintext
         because a participant's device can't encrypt. Non-blocking + dismissible
         (shared banner) so the stream stays watchable. -->
    <DismissibleBanner
      tone="warning"
      icon="🔓"
      title={$_("e2ee.compat.plaintextTitle")}
      detail={$_("e2ee.compat.plaintextDetail")}
      testid="e2ee-plaintext"
      closeLabel={$_("e2ee.settings.close")}
      bind:dismissed={e2eePlaintextBannerDismissed}
    >
      <button
        type="button"
        class="e2ee-rekey-help"
        data-testid="e2ee-plaintext-repair"
        on:click={() => {
          e2eeMsg = null;
          showE2eeSettings = true;
        }}
      >
        {$_("e2ee.settings.repair")}
      </button>
    </DismissibleBanner>
  {/if}

  {#if e2eeEnabled && e2eeKeyMissing && e2eeSessionMode !== "plaintext"}
    <!-- Keyless-recovery UX: CALM (blue, not alarming-red) "waiting for the key
         from the classroom device" banner with a live countdown. Now dismissible
         too (shared banner) so it never traps the student; collapses to a pill. -->
    <DismissibleBanner
      tone="info"
      icon="🔐"
      title={$_("e2ee.keyMissing.title")}
      detail={$_("e2ee.keyMissing.waiting", { seconds: e2eeKeyWaitSeconds })}
      hint={$_("e2ee.keyMissing.waitingHint")}
      testid="e2ee-keymissing"
      closeLabel={$_("e2ee.settings.close")}
      bind:dismissed={e2eeKeyMissingBannerDismissed}
    >
      <button
        type="button"
        class="e2ee-keymissing-repair"
        data-testid="e2ee-keymissing-repair"
        on:click={repairEncryption}
      >
        {$_("e2ee.keyMissing.repair")}
      </button>
    </DismissibleBanner>
  {/if}

  {#if e2eeEnabled && e2eeStaleKey && !showQrScanner}
    <div class="e2ee-rekey" data-testid="e2ee-rekey" role="alert">
      <div class="e2ee-rekey-card">
        <div class="e2ee-rekey-icon" aria-hidden="true">🔑</div>
        <h2 class="e2ee-rekey-title">{$_("e2ee.rekey.title")}</h2>
        <p class="e2ee-rekey-detail">{$_("e2ee.rekey.detail")}</p>
        {#if e2eeManualMode}
          <!-- Manual key entry: paste the device code when the camera fails or
               isn't available. Reachable regardless of camera state. -->
          <div class="e2ee-manual">
            <label class="e2ee-manual-label" for="e2ee-rekey-manual-input">
              {$_("e2ee.manual.label")}
            </label>
            <textarea
              id="e2ee-rekey-manual-input"
              class="e2ee-manual-input"
              data-testid="e2ee-manual-input"
              rows="3"
              placeholder={$_("e2ee.manual.placeholder")}
              bind:value={e2eeManualKey}
            ></textarea>
            <button
              type="button"
              class="e2ee-rekey-scan"
              data-testid="e2ee-manual-submit"
              on:click={submitManualKey}
            >
              {$_("e2ee.manual.submit")}
            </button>
            <button
              type="button"
              class="e2ee-manual-toggle"
              on:click={() => (e2eeManualMode = false)}
            >
              {$_("e2ee.manual.scanToggle")}
            </button>
            {#if e2eeMsg}<p class="e2ee-modal-msg">{e2eeMsg}</p>{/if}
          </div>
        {:else}
          <div class="e2ee-rekey-actions">
            <button
              type="button"
              class="e2ee-rekey-scan"
              data-testid="e2ee-rekey-scan"
              disabled={e2eeScanDisabled}
              on:click={() => {
                e2eeMsg = null;
                clearConnectError();
                showQrScanner = true;
              }}
            >
              {e2eeScanDisabled ? $_("e2ee.preparing") : $_("e2ee.scan.button")}
            </button>
            <button
              type="button"
              class="e2ee-manual-toggle"
              on:click={() => {
                e2eeMsg = null;
                e2eeManualMode = true;
              }}
            >
              {$_("e2ee.manual.toggle")}
            </button>
            <button
              type="button"
              class="e2ee-rekey-help"
              data-testid="e2ee-rekey-help"
              on:click={() => (showE2eeHelp = true)}
            >
              {$_("e2ee.help.button")}
            </button>
            {#if !e2eeForced}
              <button
                type="button"
                class="e2ee-rekey-help"
                data-testid="e2ee-rekey-plaintext"
                on:click={continueUnencrypted}
              >
                {$_("e2ee.compat.continuePlain")}
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if e2eeEnabled && showE2eeHelp}
    <div class="e2ee-help" data-testid="e2ee-help" role="dialog" aria-modal="true">
      <div class="e2ee-help-card">
        <h2 class="e2ee-help-title">{$_("e2ee.help.title")}</h2>

        <div class="e2ee-help-current" data-testid="e2ee-help-current">
          <span class="e2ee-help-current-label">{$_("e2ee.help.current")}</span>
          <strong>{$_(`e2ee.help.${e2eeHelpCurrent}.title`)}</strong>
        </div>

        <p class="e2ee-help-intro">{$_("e2ee.help.intro")}</p>

        <section class="e2ee-help-section">
          <h3>{$_("e2ee.help.status.title")}</h3>
          <p>{$_("e2ee.help.status.body")}</p>
        </section>

        {#each ["optional", "forced", "qr", "rekey", "unsupported", "blocked"] as sec (sec)}
          <section class="e2ee-help-section" class:is-current={e2eeHelpCurrent === sec}>
            <h3>{$_(`e2ee.help.${sec}.title`)}</h3>
            <p>{$_(`e2ee.help.${sec}.body`)}</p>
          </section>
        {/each}

        <div class="e2ee-help-actions">
          <button
            type="button"
            class="e2ee-help-close"
            data-testid="e2ee-help-close"
            on:click={() => (showE2eeHelp = false)}
          >
            {$_("e2ee.help.close")}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <div class="stage">
    {#if deviceOffline && offlineRoom}
      <div class="device-offline" data-testid="device-offline" role="alert">
        <div class="device-offline-card">
          <div class="device-offline-icon" aria-hidden="true">⚠</div>
          <h2 class="device-offline-title">{$_("device.offline.title")}</h2>
          <p class="device-offline-room">{offlineRoom.name}</p>
          <p class="device-offline-line" data-testid="device-offline-since">
            {$_("device.offline.lastOnline")}: {offlineSinceText}
          </p>
          <p class="device-offline-line" data-testid="device-offline-elapsed">
            {$_("device.offline.offlineFor")}: {offlineElapsedText}
          </p>
          {#if offlineWasOnlineText}
            <p class="device-offline-line" data-testid="device-offline-duration">
              {$_("device.offline.wasOnline")}: {offlineWasOnlineText}
            </p>
          {/if}
        </div>
      </div>
    {/if}
    <!-- svelte-ignore a11y-media-has-caption -->
    <video
      bind:this={videoEl}
      autoplay
      playsinline
      muted
      data-testid="video"
    ></video>
    <audio bind:this={audioEl} autoplay data-testid="audio"></audio>

    {#if !deviceVideoLive}
      <!-- Stream placeholder: the student's own avatar (rounded, centered)
           instead of a black screen while the device video is not bound. The
           existing status text still shows in the header. -->
      <div class="stream-placeholder" data-testid="stream-placeholder">
        <Avatar
          image={myProfileImage}
          name={$session.user?.display_name ?? $session.user?.email ?? null}
          size={180}
          label={$_("profile.placeholderAlt")}
        />
      </div>
    {/if}

    {#if showDemoClassScreen}
      <!-- Demo class-view: replaces the (empty) stage with a simulated classroom
           screen so the student sees how the teacher/class perceives their own
           actions — visible tile, raise-hand, mic/cam state, sent message, LED.
           Driven entirely by the local control state; no backend. -->
      <div class="demo-class-screen" data-testid="demo-class-screen">
        <ClassPreview
          title={$_("demo.classScreen.title")}
          studentName={$session.user?.display_name ?? $session.user?.email ?? ""}
          studentImage={myProfileImage}
          cameraName={selectedCamera ?? ""}
          raiseHand={raiseHandActive}
          audioOn={audioOn}
          videoOn={videoOn}
          handUpText={$_("demo.classScreen.handUp", {
            name: $session.user?.display_name ?? $session.user?.email ?? $_("demo.classScreen.you"),
          })}
          message={demoSentMessage}
        />
      </div>
    {/if}

    {#if showOverlays && viewerIdentities.length > 0}
      <!-- Co-viewer strip: small avatars of the OTHER students viewing this same
           room. Identities come from LiveKit non-device participants; images are
           fetched per-identity and cached. Sits over the video, top-left, so it
           never covers the main feed. -->
      <div
        class="viewer-strip"
        data-testid="viewer-strip"
        aria-label={$_("viewers.label")}
      >
        {#key viewerCacheVersion}
          {#each viewerIdentities as id (id)}
            <Avatar image={viewerImageCache.get(id) ?? null} name={id} size={36} />
          {/each}
        {/key}
      </div>
    {/if}

    {#if showOverlays}
    <div class="top-hud" data-testid="top-hud">
      <div class="clock" data-testid="clock" aria-label={$_("clock.label")}>
        <svg
          class="clock-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7 v5 l3 2" />
        </svg>
        <span>{clock}</span>
      </div>
      {#if sessionTime}
        <div
          class="session-time"
          data-testid="session-time"
          aria-label="Session time"
        >
          {sessionTime}
        </div>
      {/if}
      {#if e2eeEnabled}
        <button
          type="button"
          class="e2ee-hud"
          data-testid="e2ee-lock"
          data-state={e2eeOverlayState}
          aria-pressed={streamEncrypted === true}
          title={$_("e2ee.settings.title")}
          aria-label={$_("e2ee.settings.title")}
          on:click={() => {
            e2eeMsg = null;
            showE2eeSettings = true;
          }}
        >
          <EncryptionStatus
            encrypted={e2eeOverlayState === "encrypted" ||
            e2eeOverlayState === "forced"
              ? true
              : false}
          />
        </button>
      {/if}
    </div>
    {/if}

    {#if showOverlays && raiseHandActive}
      <div class="raise-hand-popup" data-testid="raise-hand-popup">
        <div class="raise-hand-popup-count">
          <span class="raise-hand-popup-num" data-testid="raise-hand-countdown"
            >{raiseHandRemaining}</span
          >
          <span class="raise-hand-popup-unit">s</span>
        </div>
        <button
          type="button"
          class="raise-hand-popup-stop"
          data-testid="raise-hand-stop"
          on:click={doRaiseHand}
        >
          {$_("raisehand.stop")}
        </button>
      </div>
    {/if}

    {#if showOverlays}
    <div class="audio-hud" data-testid="audio-hud">
      <div
        class="vu-meter"
        data-testid="vu-meter"
        data-level={audioLevel.toFixed(2)}
        aria-label={$_("vu.label")}
      >
        {#each meterCells as cell, i (i)}
          <span
            class="vu-cell"
            class:lit={cell.lit}
            data-color={cell.color}
          ></span>
        {/each}
      </div>
      <button
        type="button"
        class="audio-hud-btn"
        data-testid="sound-toggle"
        data-muted={classroomMuted}
        on:click={toggleClassroomMute}
        aria-pressed={classroomMuted}
        title={classroomMuted ? $_("sound.on.tip") : $_("sound.off.tip")}
        aria-label={classroomMuted ? $_("sound.on.tip") : $_("sound.off.tip")}
      >
        <Icon name="sound" on={!classroomMuted} />
        <span class="audio-hud-label">{classroomMuted ? $_("sound.on") : $_("sound.off")}</span>
      </button>
    </div>
    {/if}

    {#if showOverlays}
      <!-- svelte-ignore a11y-media-has-caption -->
      <video
        class="self-preview"
        class:visible={videoOn}
        bind:this={selfPreviewEl}
        autoplay
        playsinline
        muted
        data-testid="self-preview"
        aria-label={$_("selfPreview.label")}
      ></video>
      {#if videoOn}
        <div class="self-preview-tag" data-testid="self-preview-tag">
          {$_("selfPreview.tag")}
        </div>
      {/if}
    {/if}
    {#if showOverlays && adminMsg != null}
      <div class="admin-msg" data-testid="admin-msg" role="status">
        <span class="admin-msg-text">{adminMsg}</span>
        <button
          type="button"
          class="admin-msg-close"
          data-testid="admin-msg-close"
          on:click={dismissAdminMsg}
          aria-label={$_("adminMsg.dismiss")}
        >
          ×
        </button>
      </div>
    {/if}
  </div>

  <!-- Connection/error toast: a DIRECT child of `.class` (NOT nested in
       `.stage`, whose position:relative would trap it at z-index 0 behind every
       overlay/mask). position:fixed at the toast tier (z-index 100) keeps it
       visible above all masks (40), paused (60), badge (80) and the e2ee toast
       (90). -->
  {#if errorMsg}
    <div class="error" data-testid="error" data-code={errorInfo?.code ?? null}>
      <p class="error-text">{errorMsg}</p>
      {#if errorInfo}
        <!-- The stable code stays visible so the student can quote it to
             support; the raw technical string is SECONDARY (collapsed) so it
             never competes with the actionable sentence above. -->
        <p class="error-code" data-testid="error-code">
          {$_("error.connect.code", { code: errorInfo.code })}
        </p>
        {#if errorInfo.detail}
          <button
            type="button"
            class="error-details-toggle"
            data-testid="error-details-toggle"
            aria-expanded={errorDetailsOpen}
            on:click={() => (errorDetailsOpen = !errorDetailsOpen)}
          >
            {$_("error.connect.detailsShow")}
          </button>
          {#if errorDetailsOpen}
            <p class="error-detail" data-testid="error-detail">
              {errorInfo.detail}
            </p>
          {/if}
        {/if}
      {/if}
    </div>
  {/if}

  {#if showProfilePrompt}
    <ProfileImagePrompt
      name={$session.user?.display_name ?? $session.user?.email ?? null}
      on:saved={onProfileSaved}
      on:skip={onProfileSkip}
    />
  {/if}

  {#if e2eeEnabled && showE2eeSettings}
    <!-- Bug 5: centered encryption-settings popup opened from the HUD lock. -->
    <div class="e2ee-modal" data-testid="e2ee-settings-modal">
      <div class="e2ee-modal-card">
        <h2>{$_("e2ee.settings.title")}</h2>
        <div class="e2ee-settings-status">
          <EncryptionStatus
            encrypted={e2eeOverlayState === "encrypted" ||
            e2eeOverlayState === "forced"
              ? true
              : false}
          />
          <span class="e2ee-settings-status-line" data-testid="e2ee-settings-status">
            {e2eeSettingsStatus}
          </span>
        </div>
        <div class="e2ee-settings-actions">
          <button
            type="button"
            data-testid="e2ee-settings-toggle"
            disabled={e2eeSessionForced ||
              e2eeOverlayState === "unavailable" ||
              e2eeToggleBusy}
            on:click={toggleE2ee}
          >
            {#if e2eeToggleBusy}
              <span class="e2ee-spinner" aria-hidden="true"></span>
              {$_("e2ee.settings.enabling")}
            {:else}
              {streamEncrypted === true
                ? $_("e2ee.settings.disable")
                : $_("e2ee.settings.enable")}
            {/if}
          </button>
          <button
            type="button"
            data-testid="e2ee-settings-scan"
            disabled={e2eeScanDisabled}
            on:click={() => {
              showE2eeSettings = false;
              e2eeMsg = null;
              clearConnectError();
              showQrScanner = true;
            }}
          >
            {e2eeScanDisabled ? $_("e2ee.preparing") : $_("e2ee.settings.scan")}
          </button>
          {#if e2eeKeyMissing || (e2eeStaleKey && client?.hasMediaKey() !== true)}
            <!-- Keyless-recovery hub action: re-pull the key (identity present)
                 or bounce to Login to re-provision (no identity). -->
            <button
              type="button"
              data-testid="e2ee-settings-repair"
              on:click={() => {
                showE2eeSettings = false;
                void repairEncryption();
              }}
            >
              {$_("e2ee.settings.repair")}
            </button>
          {/if}
          {#if !e2eeForced}
            <button
              type="button"
              data-testid="e2ee-settings-plaintext"
              on:click={continueUnencrypted}
            >
              {$_("e2ee.compat.continuePlain")}
            </button>
          {/if}
          <button
            type="button"
            data-testid="e2ee-settings-close"
            on:click={() => (showE2eeSettings = false)}
          >
            {$_("e2ee.settings.close")}
          </button>
        </div>
      </div>
    </div>
  {/if}

  {#if e2eeEnabled && showQrScanner}
    <div class="e2ee-modal" data-testid="e2ee-scan-modal">
      <div class="e2ee-modal-card">
        <h2>{everHadKey ? $_("e2ee.rekey.title") : $_("e2ee.scan.firstRun.title")}</h2>
        <p class="e2ee-modal-help">
          {everHadKey ? $_("e2ee.rekey.detail") : $_("e2ee.scan.firstRun.body")}
        </p>
        {#if e2eeNotReady}
          <p class="e2ee-modal-msg" data-testid="e2ee-scan-preparing">
            {$_("e2ee.preparing")}
          </p>
        {/if}
        {#if e2eeManualMode}
          <!-- Manual key entry: paste the device code. Lives OUTSIDE the camera
               path so it is reachable even when the camera failed to start. -->
          <div class="e2ee-manual">
            <label class="e2ee-manual-label" for="e2ee-scan-manual-input">
              {$_("e2ee.manual.label")}
            </label>
            <textarea
              id="e2ee-scan-manual-input"
              class="e2ee-manual-input"
              data-testid="e2ee-manual-input"
              rows="3"
              placeholder={$_("e2ee.manual.placeholder")}
              bind:value={e2eeManualKey}
            ></textarea>
            <button
              type="button"
              class="e2ee-rekey-scan"
              data-testid="e2ee-manual-submit"
              on:click={submitManualKey}
            >
              {$_("e2ee.manual.submit")}
            </button>
            <button
              type="button"
              class="e2ee-manual-toggle"
              on:click={() => (e2eeManualMode = false)}
            >
              {$_("e2ee.manual.scanToggle")}
            </button>
          </div>
        {:else}
          <QrScanner on:scan={onQrScan} on:close={() => (showQrScanner = false)} />
          <button
            type="button"
            class="e2ee-manual-toggle"
            data-testid="e2ee-scan-manual-toggle"
            on:click={() => {
              e2eeMsg = null;
              e2eeManualMode = true;
            }}
          >
            {$_("e2ee.manual.toggle")}
          </button>
        {/if}
        {#if e2eeMsg}<p class="e2ee-modal-msg">{e2eeMsg}</p>{/if}
        {#if !e2eeForced}
          <button
            type="button"
            class="e2ee-rekey-help"
            data-testid="e2ee-scan-plaintext"
            on:click={continueUnencrypted}
          >
            {$_("e2ee.compat.continuePlain")}
          </button>
        {/if}
      </div>
    </div>
  {/if}

  {#if e2eeToast}
    <div
      class="e2ee-toast"
      class:e2ee-toast-success={e2eeToastTone === "success"}
      data-testid="e2ee-toast"
      role="status"
      aria-live="polite"
    >
      {e2eeToast}
    </div>
  {/if}

  {#if showOverlays}
  <div
    class="controls"
    class:invisible-only={mode === "invisible"}
    data-testid="controls"
    data-mode={mode}
  >
    {#if mode === "invisible" && !invisibleForbidden}
      <button
        type="button"
        class="go-visible-big ctl"
        data-testid="mode-toggle"
        data-mode={mode}
        on:click={toggleMode}
        disabled={!controls}
        title={$_("mode.goVisible.tip")}
        aria-label={$_("mode.goVisible.tip")}
        data-tip={$_("mode.goVisible.tip")}
      >
        <Icon name="visibility" on={true} />
        <span class="label">{$_("mode.goVisible")}</span>
      </button>
    {:else}
      {#if !invisibleForbidden}
        <button
          type="button"
          class="ctl"
          data-testid="mode-toggle"
          data-mode={mode}
          on:click={toggleMode}
          disabled={!controls}
          title={$_("mode.goInvisible.tip")}
          aria-label={$_("mode.goInvisible.tip")}
          data-tip={$_("mode.goInvisible.tip")}
        >
          <Icon name="visibility" on={false} />
          <span class="label">{$_("mode.goInvisible")}</span>
        </button>
      {/if}
      <button
        type="button"
        class="ctl"
        data-testid="raise-hand"
        data-active={raiseHandActive}
        on:click={doRaiseHand}
        disabled={!controlsEnabled}
        title={raiseHandActive ? $_("raisehand.stop.tip") : $_("raisehand.start.tip")}
        aria-label={raiseHandActive ? $_("raisehand.stop.tip") : $_("raisehand.start.tip")}
        data-tip={raiseHandActive ? $_("raisehand.stop.tip") : $_("raisehand.start.tip")}
      >
        <Icon name="hand" on={raiseHandActive} />
        <span class="label">{raiseHandActive ? $_("raisehand.stop") : $_("raisehand.start")}</span>
      </button>
      <button
        type="button"
        class="ctl"
        data-testid="audio-toggle"
        data-on={audioOn}
        on:click={toggleAudio}
        disabled={!controlsEnabled}
        title={audioOn ? $_("audio.off.tip") : $_("audio.on.tip")}
        aria-label={audioOn ? $_("audio.off.tip") : $_("audio.on.tip")}
        data-tip={audioOn ? $_("audio.off.tip") : $_("audio.on.tip")}
      >
        <Icon name="mic" on={audioOn} />
        <span class="label">{audioOn ? $_("audio.off") : $_("audio.on")}</span>
      </button>
      <button
        type="button"
        class="ctl"
        data-testid="video-toggle"
        data-on={videoOn}
        on:click={toggleVideo}
        disabled={!controlsEnabled}
        title={videoOn ? $_("video.off.tip") : $_("video.on.tip")}
        aria-label={videoOn ? $_("video.off.tip") : $_("video.on.tip")}
        data-tip={videoOn ? $_("video.off.tip") : $_("video.on.tip")}
      >
        <Icon name="camera" on={videoOn} />
        <span class="label">{videoOn ? $_("video.off") : $_("video.on")}</span>
      </button>
      <button
        type="button"
        class="ctl"
        data-testid="text-open"
        on:click={openText}
        disabled={!controls || textOpen}
        title={$_("text.open.tip")}
        aria-label={$_("text.open.tip")}
        data-tip={$_("text.open.tip")}
      >
        <Icon name="text" />
        <span class="label">{$_("text.open")}</span>
      </button>
      {#if e2eeEnabled}
        <button
          type="button"
          class="ctl"
          data-testid="e2ee-scan-open"
          disabled={e2eeScanDisabled}
          on:click={() => {
            e2eeMsg = null;
            clearConnectError();
            showQrScanner = true;
          }}
          title={e2eeScanDisabled ? $_("e2ee.preparing") : $_("e2ee.scan.title")}
          aria-label={e2eeScanDisabled ? $_("e2ee.preparing") : $_("e2ee.scan.title")}
          data-tip={e2eeScanDisabled ? $_("e2ee.preparing") : $_("e2ee.scan.title")}
        >
          <Icon name="lock" on={false} />
          <span class="label"
            >{e2eeScanDisabled ? $_("e2ee.preparing") : $_("e2ee.scan.button")}</span
          >
        </button>
      {/if}
    {/if}
  </div>
  {/if}

  {#if textOpen}
    <div class="text-overlay" data-testid="text-overlay">
      <textarea
        bind:this={textareaEl}
        bind:value={textValue}
        data-testid="text-input"
        placeholder={$_("text.placeholder")}
        rows="4"
      ></textarea>
      <div class="row">
        <button type="button" data-testid="text-send" on:click={sendText}
          >{$_("text.send")}</button
        >
        <button type="button" data-testid="text-close" on:click={closeText}
          >{$_("text.close")}</button
        >
      </div>
    </div>
  {/if}

  {#if videoDialogOpen}
    <div
      class="video-dialog-backdrop"
      data-testid="video-confirm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-confirm-title"
    >
      <div class="video-dialog">
        <h2 id="video-confirm-title">{$_("video.confirm.title")}</h2>
        <div class="video-dialog-preview">
          {#if previewError}
            <p class="error" data-testid="video-confirm-error">{previewError}</p>
          {:else if previewStarting}
            <p class="muted">{$_("video.confirm.starting")}</p>
          {/if}
          <!-- svelte-ignore a11y-media-has-caption -->
          <video
            bind:this={confirmPreviewEl}
            autoplay
            playsinline
            muted
            data-testid="video-confirm-preview"
          ></video>
        </div>
        {#if videoInputs.length > 1}
          <label class="video-dialog-camera">
            <span class="muted">{$_("video.confirm.camera")}</span>
            <select
              class="camera-select"
              data-testid="video-confirm-camera"
              bind:value={selectedDeviceId}
              on:change={() => selectPreviewCamera(selectedDeviceId ?? "")}
            >
              {#each videoInputs as cam, i (cam.deviceId)}
                <option value={cam.deviceId}>
                  {cam.label || `${$_("video.confirm.camera")} ${i + 1}`}
                </option>
              {/each}
            </select>
          </label>
        {/if}
        <p class="muted">{$_("video.confirm.preview")}</p>
        <div class="row">
          <button
            type="button"
            data-testid="video-confirm-cancel"
            on:click={cancelVideoDialog}
          >
            {$_("video.confirm.cancel")}
          </button>
          <button
            type="button"
            class="primary"
            data-testid="video-confirm-send"
            on:click={confirmVideoDialog}
            disabled={!previewStream}
          >
            {$_("video.confirm.send")}
          </button>
        </div>
      </div>
    </div>
  {/if}
</section>
{/if}

<style>
  .class {
    display: grid;
    grid-template-rows: auto 1fr auto;
    /* Constrain the single column to the viewport width. Without this the
       implicit column is `auto`-sized to the widest child's max-content (the
       non-wrapping controls bar), blowing the whole layout out past the
       viewport on narrow screens — the row never shrinks so flex-wrap on
       `.controls` never engages, and everything shifts/overflows right. */
    grid-template-columns: minmax(0, 1fr);
    /* Dynamic viewport height so the mobile URL bar doesn't create overflow.
       Fallback order (last supported wins): 100vh → 100dvh → the live
       visual-viewport height published as --app-height by syncAppHeight().
       The var() default keeps the dvh value on browsers without
       window.visualViewport. Without the last step iOS Safari leaves a strip of
       page background under the bottom controls bar, because dvh is the
       *largest* dynamic height, not the currently visible one. */
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }
  /* The var() step is gated: on a browser without dvh support the fallback
     `100dvh` inside var() is invalid at computed-value time, which resolves the
     whole declaration to `height: auto` (it does NOT fall back to the earlier
     100vh line). Behind @supports, the declaration only exists where dvh works,
     so old browsers keep the 100vh above. */
  @supports (height: 100dvh) {
    .class {
      height: var(--app-height, 100dvh);
    }
  }
  /* Grid items default to min-width:auto, which lets their content force the
     track wider than the viewport. Pin them to 0 so the column cap holds. */
  .class > * {
    min-width: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    /* Clear the status-bar / notch on devices with viewport-fit=cover, where
       the page draws under the system bar — otherwise the header sits too high
       (under the clock/notch). Falls back to the base 0.75rem when there is no
       inset. */
    padding-top: max(0.75rem, env(safe-area-inset-top));
    background: #111827;
    border-bottom: 1px solid #374151;
    /* Keep the header (profile/logout buttons) above the room-gate overlay
       (z-index: 40) so it stays reachable while a room is being picked. */
    position: relative;
    z-index: 41;
  }
  /* Secondary header actions. Desktop: `display: contents` dissolves the
     wrapper so its children remain direct flex items of <header> — the layout
     is byte-for-byte the pre-hamburger one. */
  .header-actions {
    display: contents;
  }
  /* Hamburger is mobile-only; hidden at/above the breakpoint. */
  .menu-toggle {
    display: none;
  }
  @media (max-width: 640px) {
    /* Narrow phones: collapse the secondary actions into a dropdown anchored
       under the header. The header already pads for env(safe-area-inset-top),
       so `top: 100%` lands below the notch automatically. */
    .header-actions {
      display: none;
      position: absolute;
      top: 100%;
      right: 0.5rem;
      /* Header itself is z-index 41 and (being positioned) a stacking context,
         so this panel paints above the header's own content while the whole
         header still stays below .paused-overlay (60) and .admin-msg (100). */
      z-index: 50;
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      min-width: 12rem;
      max-width: min(16rem, calc(100vw - 1rem));
      margin-top: 0.4rem;
      padding: 0.5rem;
      background: #111827;
      border: 1px solid #374151;
      border-radius: 10px;
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
    }
    .header-actions.open {
      display: flex;
    }
    /* Full-width targets inside the sheet. */
    .header-actions > button,
    .header-actions .camera-select {
      width: 100%;
      max-width: none;
      text-align: center;
    }
    .header-actions .clean-view-btn {
      justify-content: center;
    }
    .menu-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 40px;
      height: 36px;
      padding: 0;
    }
    /* Three bars drawn with the border-box + pseudo-free trick: one element,
       current color, no extra markup or icons. */
    .menu-bars,
    .menu-bars::before,
    .menu-bars::after {
      display: block;
      width: 18px;
      height: 2px;
      background: #f3f4f6;
      border-radius: 1px;
    }
    .menu-bars {
      position: relative;
    }
    .menu-bars::before,
    .menu-bars::after {
      content: "";
      position: absolute;
      left: 0;
    }
    .menu-bars::before {
      top: -6px;
    }
    .menu-bars::after {
      top: 6px;
    }
  }
  .who {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    padding: 0.2rem 0.4rem;
    margin: -0.2rem -0.4rem;
    background: none;
    border: none;
    border-radius: 999px;
    color: inherit;
    font-size: inherit;
    cursor: pointer;
    max-width: 45vw;
  }
  .who:hover {
    background: #1f2937;
  }
  .who:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 1px;
  }
  .who-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Tight screens: keep the avatar thumbnail, drop the name text. */
  @media (max-width: 480px) {
    .who-name {
      display: none;
    }
  }
  /* Persistent Test-Modus badge in the header (demo principal). */
  .test-mode-badge {
    padding: 0.15rem 0.6rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #04221a;
    background: #fbbf24;
    border-radius: 999px;
    white-space: nowrap;
  }
  /* Demo class-view: simulated classroom screen filling the stage in demo mode. */
  .demo-class-screen {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    /* Extra top padding clears the clock/session-time HUD (top-left, z-index 7)
       so the preview's header title never collides with it. */
    padding: 46px 12px 12px;
    background: #0b1220;
    box-sizing: border-box;
  }
  .demo-class-screen :global(.class-preview) {
    flex: 1 1 auto;
  }
  .status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #d1d5db;
    margin-left: auto;
    margin-right: 0.5rem;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #f59e0b;
  }
  .dot.on {
    background: #10b981;
  }
  button {
    padding: 0.4rem 0.75rem;
    background: #374151;
    color: #f3f4f6;
    border: 1px solid #4b5563;
    border-radius: 6px;
  }
  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  /* Room selector as a centered modal popup (mirrors .e2ee-modal). */
  .room-gate {
    position: fixed;
    inset: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.6);
  }
  .room-gate-card {
    background: #1c1c1e;
    color: #fff;
    padding: 1.5rem;
    border-radius: 14px;
    max-width: 520px;
    width: min(520px, 92vw);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  }
  .gate-msg {
    color: #d1d5db;
    margin: 0;
    text-align: center;
  }
  .room-picker {
    width: 100%;
    display: grid;
    gap: 1rem;
  }
  .room-picker h2 {
    margin: 0;
    font-size: 1.15rem;
    text-align: center;
  }
  .room-list {
    display: grid;
    gap: 0.5rem;
  }
  .room-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: #1f2937;
    border: 1px solid #4b5563;
    border-radius: 10px;
    text-align: left;
  }
  .room-name {
    font-weight: 600;
  }
  .room-visibility {
    font-size: 0.8rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .no-rooms {
    display: grid;
    gap: 0.75rem;
    justify-items: center;
    text-align: center;
    color: #d1d5db;
  }
  .no-rooms p {
    margin: 0;
  }
  .error-inline {
    color: #fca5a5;
  }
  /* Opaque "device not online" banner scoped to the stage (stream) area so the
     topbar stays reachable (logout / profile / leave-room) while the kiosk is down. */
  .device-offline {
    position: absolute;
    inset: 0;
    z-index: 30;
    display: grid;
    place-items: center;
    padding: 1.5rem;
    background: #0b1220;
    text-align: center;
  }
  .device-offline-card {
    width: min(560px, 94vw);
    display: grid;
    gap: 0.6rem;
    justify-items: center;
  }
  .device-offline-icon {
    font-size: clamp(3rem, 14vw, 6rem);
    line-height: 1;
    color: #f59e0b;
  }
  .device-offline-title {
    margin: 0;
    font-size: clamp(1.5rem, 6vw, 2.5rem);
    color: #f3f4f6;
  }
  .device-offline-room {
    margin: 0;
    font-weight: 600;
    font-size: clamp(1.1rem, 4vw, 1.5rem);
    color: #93c5fd;
  }
  .device-offline-line {
    margin: 0;
    font-size: clamp(0.95rem, 3.5vw, 1.15rem);
    color: #d1d5db;
  }
  /* Teacher Pause: fullscreen blocking overlay above the stage (z above the
     device-offline banner) so the kid sees a clear "paused" state instead of
     frozen/black video. Modeled on .device-offline. */
  .paused-overlay {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: grid;
    place-items: center;
    padding: 1.5rem;
    background: #0b1220;
    text-align: center;
  }
  .paused-card {
    width: min(560px, 94vw);
    display: grid;
    gap: 0.75rem;
    justify-items: center;
  }
  .paused-icon {
    font-size: clamp(3rem, 14vw, 6rem);
    line-height: 1;
    color: #f59e0b;
  }
  .paused-title {
    margin: 0;
    font-size: clamp(1.5rem, 6vw, 2.5rem);
    color: #f3f4f6;
  }
  .paused-message {
    margin: 0;
    font-size: clamp(1.05rem, 4vw, 1.5rem);
    color: #93c5fd;
  }
  .stage {
    position: relative;
    background: black;
    display: grid;
    place-items: center;
    overflow: hidden;
    min-height: 0;
  }
  video {
    width: 100%;
    height: 100%;
    max-height: 100%;
    object-fit: contain;
    background: black;
  }
  /* Stream placeholder: the student's own avatar centered on the black stage
     while no device camera is bound. Sits under the HUD overlays (z below 7). */
  .stream-placeholder {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    z-index: 1;
    pointer-events: none;
  }
  /* Co-viewer avatar strip: a small responsive row of the OTHER students
     viewing the same room. Bottom-left so it clears the top HUD and the main
     feed. Wraps on narrow screens. */
  .viewer-strip {
    position: absolute;
    left: 12px;
    bottom: 12px;
    display: flex;
    flex-flow: row wrap;
    gap: 6px;
    max-width: 60%;
    z-index: 7;
    pointer-events: none;
  }
  /* Top-left heads-up display: audio (VU) meter | wall clock | session timer,
     laid out left-to-right in a single overlay row. */
  .e2ee-hud {
    display: inline-flex;
    align-items: center;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
    /* Critical: the parent .top-hud sets pointer-events:none so the overlay row
       never blocks the video; re-enable clicks on the lock button itself, else
       the encryption-settings hub is unreachable. */
    pointer-events: auto;
  }
  .e2ee-hud:focus-visible {
    outline: 2px solid #60a5fa;
    outline-offset: 2px;
    border-radius: 4px;
  }
  /* Blocked-join remediation screen (forced E2EE + incapable client). */
  .e2ee-blocked {
    position: fixed;
    inset: 0;
    z-index: 70;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.8);
    padding: 1rem;
  }
  .e2ee-blocked-card {
    background: #1c1c1e;
    color: #fff;
    padding: 1.75rem;
    border-radius: 14px;
    max-width: 440px;
    width: 100%;
    text-align: center;
  }
  .e2ee-blocked-icon {
    font-size: 2.5rem;
  }
  .e2ee-blocked-title {
    margin: 0.5rem 0;
  }
  .e2ee-blocked-detail {
    opacity: 0.9;
  }
  .e2ee-blocked-steps {
    text-align: left;
    margin: 1rem auto;
    max-width: 360px;
    line-height: 1.5;
  }
  .e2ee-blocked-retry {
    background: #2563eb;
    color: #fff;
    border: none;
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 0.5rem;
  }
  .e2ee-rekey {
    position: fixed;
    inset: 0;
    /* Stay BELOW the header (z-index: 41) so Leave-room / Profile remain
       reachable — project rule: popups never cover the main header bar. */
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.8);
    padding: 1rem;
  }
  .e2ee-rekey-card {
    background: #1c1c1e;
    color: #fff;
    padding: 1.75rem;
    border-radius: 14px;
    max-width: 420px;
    width: 100%;
    text-align: center;
  }
  .e2ee-rekey-icon {
    font-size: 2.5rem;
  }
  .e2ee-rekey-title {
    margin: 0.5rem 0;
  }
  .e2ee-rekey-detail {
    opacity: 0.9;
  }
  .e2ee-rekey-scan {
    background: #2563eb;
    color: #fff;
    border: none;
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    cursor: pointer;
  }
  .e2ee-rekey-actions {
    display: flex;
    flex-flow: row wrap;
    gap: 0.5rem;
    justify-content: center;
    margin-top: 0.75rem;
  }
  .e2ee-rekey-help {
    background: transparent;
    color: #fff;
    border: 1px solid #4b5563;
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    cursor: pointer;
  }
  .e2ee-modal {
    position: fixed;
    inset: 0;
    /* Stay BELOW the header (z-index: 41) so Leave-room / Profile remain
       reachable — project rule: popups never cover the main header bar. */
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.6);
  }
  .e2ee-modal-card {
    background: #1c1c1e;
    color: #fff;
    padding: 1.25rem;
    border-radius: 12px;
    max-width: 360px;
    width: 90%;
  }
  .e2ee-modal-card h2 {
    margin: 0 0 0.5rem;
    font-size: 1.1rem;
  }
  .e2ee-modal-help {
    font-size: 0.9rem;
    opacity: 0.8;
    margin: 0 0 0.75rem;
  }
  .e2ee-help {
    position: fixed;
    inset: 0;
    /* Stay BELOW the header (z-index: 41) so Leave-room / Profile remain
       reachable — project rule: popups never cover the main header bar. */
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.7);
    padding: 1rem;
  }
  .e2ee-help-card {
    background: #1c1c1e;
    color: #fff;
    padding: 1.5rem;
    border-radius: 14px;
    max-width: 560px;
    width: 100%;
    max-height: 80dvh;
    overflow-y: auto;
    text-align: left;
  }
  .e2ee-help-title {
    margin: 0 0 0.75rem;
    font-size: 1.25rem;
  }
  .e2ee-help-current {
    background: #0b3a67;
    border: 1px solid #2563eb;
    border-radius: 10px;
    padding: 0.6rem 0.85rem;
    margin-bottom: 1rem;
    font-size: 0.95rem;
  }
  .e2ee-help-current-label {
    display: block;
    opacity: 0.8;
    font-size: 0.8rem;
    margin-bottom: 0.15rem;
  }
  .e2ee-help-intro {
    opacity: 0.9;
    line-height: 1.5;
    margin: 0 0 1rem;
  }
  .e2ee-help-section {
    border-top: 1px solid #374151;
    padding: 0.85rem 0;
  }
  .e2ee-help-section h3 {
    margin: 0 0 0.3rem;
    font-size: 1rem;
  }
  .e2ee-help-section p {
    margin: 0;
    opacity: 0.9;
    line-height: 1.5;
    font-size: 0.92rem;
  }
  .e2ee-help-section.is-current {
    background: rgba(37, 99, 235, 0.12);
    border-left: 3px solid #2563eb;
    padding-left: 0.75rem;
    border-radius: 6px;
  }
  .e2ee-help-actions {
    margin-top: 1.25rem;
    display: flex;
    justify-content: flex-end;
  }
  .e2ee-help-close {
    background: #2563eb;
    color: #fff;
    border: none;
    padding: 0.5rem 1.25rem;
    border-radius: 8px;
    cursor: pointer;
  }
  .e2ee-modal-msg {
    margin-top: 0.5rem;
    font-size: 0.9rem;
  }
  /* Phase 1: recovery-code input inside the recovery prompt. */
  .e2ee-recovery-input {
    width: 100%;
    margin: 0.75rem 0;
    padding: 0.6rem 0.75rem;
    border-radius: 8px;
    border: 1px solid #4b5563;
    background: #111827;
    color: #fff;
    font-size: 1rem;
    box-sizing: border-box;
  }
  /* Banner container styles (plaintext-downgrade / not-ready / keyless-waiting)
     now live in the shared DismissibleBanner component. Only the slotted action
     button (.e2ee-keymissing-repair, .e2ee-rekey-help) styling stays here. */
  .e2ee-keymissing-repair {
    background: #2563eb;
    color: #fff;
    border: none;
    padding: 0.45rem 0.9rem;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    align-self: center;
  }
  /* Manual key entry (paste-the-code fallback for scan/rekey modals). */
  .e2ee-manual {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0.5rem 0;
  }
  .e2ee-manual-label {
    font-size: 0.85rem;
    opacity: 0.85;
  }
  .e2ee-manual-input {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border-radius: 8px;
    border: 1px solid #4b5563;
    background: #111827;
    color: #fff;
    font-size: 0.95rem;
    box-sizing: border-box;
    resize: vertical;
  }
  .e2ee-manual-toggle {
    background: transparent;
    border: none;
    color: #93c5fd;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 0.25rem 0;
    align-self: flex-start;
  }
  /* Scan-result toast: lives above everything but is non-interactive, so it
     never blocks the header buttons it floats over. */
  .e2ee-toast {
    position: fixed;
    top: calc(env(safe-area-inset-top) + 0.75rem);
    left: 50%;
    transform: translateX(-50%);
    z-index: 90;
    max-width: min(92vw, 420px);
    background: #1c1c1e;
    color: #fff;
    padding: 0.6rem 1rem;
    border-radius: 10px;
    border: 1px solid #374151;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
    font-size: 0.9rem;
    text-align: center;
    pointer-events: none;
  }
  .e2ee-toast-success {
    background: #16a34a;
    color: #ffffff;
  }
  /* Bug 5: encryption-settings popup status row + action buttons. */
  .e2ee-settings-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 1rem;
  }
  .e2ee-settings-status-line {
    font-size: 0.95rem;
  }
  .e2ee-settings-actions {
    display: flex;
    flex-flow: row wrap;
    gap: 0.5rem;
  }
  .e2ee-spinner {
    display: inline-block;
    width: 0.9em;
    height: 0.9em;
    margin-right: 0.4em;
    vertical-align: -0.1em;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: e2ee-spin 0.9s linear infinite;
  }
  @keyframes e2ee-spin {
    to {
      transform: rotate(360deg);
    }
  }
  /* Bug 9: icon-only clean-view toggle in the header. */
  .clean-view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.4rem;
  }

  .top-hud {
    position: absolute;
    top: 12px;
    left: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    z-index: 7;
    pointer-events: none;
  }
  .clock {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #f3f4f6;
    background: rgba(0, 0, 0, 0.55);
    border-radius: 6px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  }
  .clock-icon {
    width: 0.95em;
    height: 0.95em;
    flex: 0 0 auto;
    opacity: 0.9;
  }
  /* Running session time, shown right of ("behind") the wall clock. */
  .session-time {
    padding: 2px 8px;
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #cbd5e1;
    background: rgba(0, 0, 0, 0.45);
    border-radius: 6px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  }
  /* Camera switcher: lives in the navigation header bar. Touch-friendly height
     for students; only shown when >1 camera exists. */
  .camera-select {
    min-height: 36px;
    max-width: min(40vw, 220px);
    padding: 4px 28px 4px 32px;
    font-size: 14px;
    font-weight: 600;
    color: #f3f4f6;
    background: #374151;
    border: 1px solid #4b5563;
    border-radius: 8px;
    appearance: none;
    /* Left: camera glyph (matches Icon.svelte "camera"). Right: dropdown chevron. */
    background-image:
      url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23f3f4f6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M2 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2Z'/><path d='M17 9l5-3v12l-5-3Z'/></svg>"),
      url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23f3f4f6' stroke-width='3'><path d='M6 9l6 6 6-6'/></svg>");
    background-repeat: no-repeat, no-repeat;
    background-position: left 8px center, right 8px center;
  }
  .camera-select:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 2px;
  }
  /* VU meter: leftmost element of the top-left HUD row. */
  .vu-meter {
    display: flex;
    flex-direction: row;
    gap: 2px;
    padding: 4px 6px;
    background: rgba(0, 0, 0, 0.55);
    border-radius: 6px;
  }
  .vu-cell {
    width: 6px;
    height: 18px;
    background: #1f2937;
    border-radius: 1px;
    transition: background 60ms linear;
  }
  .vu-cell.lit[data-color="green"] {
    background: #10b981;
    box-shadow: 0 0 4px rgba(16, 185, 129, 0.6);
  }
  .vu-cell.lit[data-color="yellow"] {
    background: #f59e0b;
    box-shadow: 0 0 4px rgba(245, 158, 11, 0.6);
  }
  .vu-cell.lit[data-color="red"] {
    background: #ef4444;
    box-shadow: 0 0 4px rgba(239, 68, 68, 0.6);
  }
  /* Raise-hand popup: stacked below the top-left HUD row (VU | clock | session). */
  .raise-hand-popup {
    position: absolute;
    top: 52px;
    left: 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    width: min(180px, 60%);
    padding: 12px;
    background: rgba(17, 24, 39, 0.92);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.5);
    z-index: 8;
  }
  .raise-hand-popup-count {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 2px;
    color: #f3f4f6;
  }
  .raise-hand-popup-num {
    font-size: 40px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .raise-hand-popup-unit {
    font-size: 18px;
    font-weight: 600;
    color: #9ca3af;
  }
  .raise-hand-popup-stop {
    width: 100%;
    min-height: 48px;
    padding: 0.6rem 0.75rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: #fff;
    background: #ef4444;
    border: 1px solid #dc2626;
    border-radius: 8px;
  }
  .raise-hand-popup-stop:hover {
    background: #dc2626;
  }
  .audio-hud {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    z-index: 7;
    pointer-events: none;
  }
  .audio-hud-btn {
    pointer-events: auto;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 56px;
    min-height: 56px;
    padding: 6px 8px;
    font: inherit;
    color: #f3f4f6;
    background: rgba(0, 0, 0, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    cursor: pointer;
    transition:
      background 120ms ease,
      border-color 120ms ease;
  }
  .audio-hud-btn :global(svg) {
    width: 1.6em;
    height: 1.6em;
  }
  .audio-hud-btn[data-muted="true"] {
    color: #fecaca;
    background: rgba(127, 29, 29, 0.85);
    border-color: #ef4444;
  }
  .audio-hud-btn[data-muted="false"] {
    color: #d1fae5;
    background: rgba(6, 95, 70, 0.85);
    border-color: #10b981;
  }
  .audio-hud-btn:hover {
    filter: brightness(1.1);
  }
  .audio-hud-btn:focus-visible {
    outline: 2px solid #10b981;
    outline-offset: 2px;
  }
  .audio-hud-label {
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  }
  .self-preview {
    position: absolute;
    right: 12px;
    bottom: 12px;
    width: 22%;
    max-width: 220px;
    max-height: 30%;
    object-fit: cover;
    border: 2px solid #10b981;
    border-radius: 8px;
    background: #111;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    display: none;
    z-index: 5;
  }
  .self-preview.visible {
    display: block;
  }
  .self-preview-tag {
    position: absolute;
    right: 16px;
    bottom: calc(12px + 6px);
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: rgba(239, 68, 68, 0.85);
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    pointer-events: none;
    z-index: 6;
  }
  .error {
    /* Fixed to the viewport (not the `.stage` stacking context) and at the
       top toast tier so the connection error always floats ABOVE every
       overlay/mask (masks 40, paused 60, badge 80, e2ee-toast 90). */
    position: fixed;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    max-width: min(92vw, 420px);
    text-align: center;
    z-index: 100;
    color: #fca5a5;
    background: rgba(0, 0, 0, 0.6);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
  }
  .error-text {
    margin: 0;
  }
  .error-code {
    margin: 0.35rem 0 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
    opacity: 0.75;
  }
  .error-details-toggle {
    margin-top: 0.25rem;
    background: none;
    border: none;
    padding: 0;
    color: inherit;
    font-size: 0.72rem;
    text-decoration: underline;
    cursor: pointer;
    opacity: 0.7;
  }
  .error-detail {
    margin: 0.25rem 0 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.7rem;
    text-align: left;
    word-break: break-word;
    opacity: 0.7;
  }
  .admin-msg {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 0.75rem;
    max-width: min(560px, 90%);
    padding: 0.6rem 0.75rem 0.6rem 1rem;
    background: #1d4ed8;
    color: #fff;
    font-weight: 600;
    border-radius: 10px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
    z-index: 9;
  }
  .admin-msg-text {
    flex: 1;
    word-break: break-word;
  }
  .admin-msg-close {
    flex: none;
    width: 28px;
    height: 28px;
    padding: 0;
    line-height: 1;
    font-size: 1.2rem;
    color: #fff;
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
  }
  .controls {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
    background: #111827;
    border-top: 1px solid #374151;
    flex-wrap: wrap;
  }
  /* Invisible mode: only the big "Go visible" button is shown, centered. */
  .controls.invisible-only {
    justify-content: center;
    padding: 1.25rem 1rem;
    padding-bottom: max(1.25rem, env(safe-area-inset-bottom));
  }

  /* Control button: inline icon + label, relative anchor for the tooltip. */
  .ctl {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    position: relative;
  }
  .ctl :global(svg) {
    flex: 0 0 auto;
  }

  /* Status colors via the existing data hooks. High specificity (.controls .ctl)
     so they win over the base `button` rule. */
  /* ON / active = green */
  .controls .ctl[data-on="true"],
  .controls .ctl[data-active="true"] {
    background: #065f46;
    border-color: #10b981;
    color: #ecfdf5;
  }
  /* OFF / muted = muted red-gray */
  .controls .ctl[data-on="false"] {
    background: #3f2937;
    border-color: #7f1d1d;
    color: #fecaca;
  }

  /* Custom tooltip bubble (hover + keyboard focus). Renders above the bar. */
  .ctl[data-tip]::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    min-width: max-content;
    max-width: 240px;
    white-space: normal;
    text-align: center;
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    font-weight: 500;
    line-height: 1.25;
    color: #f9fafb;
    background: #1f2937;
    border: 1px solid #4b5563;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease;
    z-index: 30;
  }
  .ctl[data-tip]:hover::after,
  .ctl[data-tip]:focus-visible::after {
    opacity: 1;
  }
  .ctl[data-tip]:disabled::after {
    display: none;
  }

  .go-visible-big {
    width: min(420px, 100%);
    min-height: 64px;
    padding: 1rem 1.5rem;
    font-size: 1.4rem;
    font-weight: 800;
    color: #fff;
    background: #10b981;
    border: 1px solid #059669;
    border-radius: 12px;
  }
  .go-visible-big.ctl {
    justify-content: center;
  }
  .go-visible-big:hover:not(:disabled) {
    background: #059669;
  }

  /* Narrow screens: icon-only, keep tooltips. Keep the primary CTA labelled. */
  @media (max-width: 480px) {
    .controls .ctl .label {
      display: none;
    }
    .controls .ctl:not(.go-visible-big) {
      padding: 0.5rem;
      min-width: 44px;
      justify-content: center;
    }
    .controls .go-visible-big .label {
      display: inline;
    }
  }
  .text-overlay {
    position: fixed;
    left: 50%;
    bottom: 4rem;
    transform: translateX(-50%);
    background: #1f2937;
    border: 1px solid #4b5563;
    border-radius: 10px;
    padding: 0.75rem;
    display: grid;
    gap: 0.5rem;
    width: min(520px, 90vw);
    z-index: 30;
  }
  .text-overlay textarea {
    background: #111827;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 6px;
    padding: 0.5rem;
    font: inherit;
    resize: vertical;
  }
  .row {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }
  .video-dialog-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: grid;
    place-items: center;
    z-index: 40;
  }
  .video-dialog {
    background: #1f2937;
    border: 1px solid #4b5563;
    border-radius: 12px;
    padding: 1rem;
    width: min(420px, 92vw);
    display: grid;
    gap: 0.75rem;
    color: #f3f4f6;
  }
  .video-dialog h2 {
    margin: 0;
    font-size: 1.1rem;
  }
  .video-dialog .muted {
    margin: 0;
    color: #9ca3af;
    font-size: 0.9rem;
  }
  .video-dialog-preview {
    position: relative;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 4 / 3;
    display: grid;
    place-items: center;
  }
  .video-dialog-preview video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .video-dialog-preview .error,
  .video-dialog-preview .muted {
    position: absolute;
    color: #f3f4f6;
    padding: 0.25rem 0.5rem;
    z-index: 1;
  }
  .video-dialog-preview .error {
    color: #fca5a5;
  }
  .video-dialog-camera {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin-top: 0.5rem;
  }
  .video-dialog-camera .camera-select {
    max-width: none;
    width: 100%;
  }
  .video-dialog button.primary {
    background: #10b981;
    border-color: #059669;
    font-weight: 600;
  }
  .video-dialog button.primary:disabled {
    background: #374151;
    border-color: #4b5563;
  }
</style>
